import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Routes, Route, useNavigate, useParams } from "react-router";
import { App, RequirePolicy, createEmbed } from "@livefree/applications";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor, useErrorReactor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, Sorting, RequestBrokerDataGrid, RequestBroker, Paging } from "@livefree/request-broker";
import { RestClient } from "@livefree/js-sdk";
import { EnvironmentConfigurations, useRuntimeProvider, useComponent } from "@livefree/runtime";
import { Chip, Spinner, Button, Select, ListBox, Label, Description, Breadcrumbs, BreadcrumbsItem, Card, Table, Alert, Tooltip, Checkbox, Tabs } from "@heroui/react";
import { useCallback, useMemo, useRef, useState, Fragment as Fragment$1, createElement } from "react";
import { Filters, FilterSelect, FilterInput, FilterDateRange, FilterConstant, FilterNumericInput, HorizontalFilters, HorizontalChips } from "@livefree/filters";
import { parseAndFormatDateTime, toISO } from "@livefree/common";
import { withDrawerController, SmsSegments, PhoneInput, DrawerActions, DeleteButton, ReloadButton, substitutePlaceholders, CopyToClipboard, TextFieldWithCopy, ToggleButtons, Forbidden } from "@livefree/react-ui";
import { Timeline, Segment, Stepper } from "@heroui-pro/react";
import { RichTextEditor, useRichTextEditor } from "@heroui-pro/react/rich-text-editor";
import { KPI } from "@heroui-pro/react/kpi";
import { KPIGroup } from "@heroui-pro/react/kpi-group";
import { CsvImporter, ImporterField } from "@livefree/csv";
const reactive = new ReactiveCore("notifications");
const NotificationChannel = {
  Email: 1,
  Sms: 2,
  Voice: 3,
  Dialer: 4
};
const NotificationStatusType = {
  Pending: 1,
  Scheduled: 2,
  Queued: 3,
  Sent: 4,
  Delivered: 5,
  Failed: 6,
  Bounced: 7,
  Suppressed: 8,
  Canceled: 9,
  Sending: 10
};
const NotificationBatchStatusType = {
  Submitted: 1,
  Processing: 2,
  Completed: 3
};
const TeamsDestinationKind = {
  /** The flow posts to one fixed channel. */
  Channel: 1,
  /** The flow posts, as the Workflows bot, to the person named in the request. */
  DirectMessage: 2
};
class NotificationsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "notifications");
  }
  /** `POST /notifications` — submit a new notification for delivery (or scheduling). */
  async submit(request) {
    return await this.create(request);
  }
  /** `GET /notifications/{id}` — fetch a single notification by id. */
  async getById(id) {
    return await this.fetch(id);
  }
  /** `POST /notifications/{id}/cancel` — cancel a pending or scheduled notification. */
  async cancel(id) {
    return await this.httpPOST({ url: this.appendUrl(`/${id}/cancel`) });
  }
  /** `GET /notifications/{id}/history` — list the status-change history for a notification. */
  async getHistory(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/history`) });
  }
  /**
   * `POST /notifications/query` — run an MQL query over notifications and get back a page of
   * results (`values` plus `totalCount`/`skip`/`take`).
   *
   * Pass the request-broker `metadata` (the filters/sorting/paging the frontend collected); it's
   * compiled to an MQL string via {@link MqlRequestBuilder} before invoking the API. With no
   * metadata, all notifications are fetched. Named `queryMql` because `query` is reserved on
   * `BaseEndpoint`.
   *
   * `_actionKey` is the request-broker key of the list that asked (one per scope); it is not sent,
   * but it rides along in the action's args so the reactor can record the count under that key.
   */
  async queryMql(metadata, _actionKey) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("NotificationModel") : "FETCH NotificationModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query
    });
  }
  /**
   * `POST /notifications/query` — fetch the notifications belonging to a batch, newest first.
   * The MQL is built directly (with `BatchId` baked into the MATCH) rather than from request-broker
   * metadata, since this is a fixed, batch-scoped query.
   */
  async queryByBatch(batchId, skip = 0, take = 200) {
    const query = `FETCH NotificationModel MATCH[BatchId == ${batchId}] SORTBY CreatedOnUtc DESC SKIP ${skip} TAKE ${take} WITH COUNT;`;
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query
    });
  }
}
class NotificationBatchesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "notifications/batches");
  }
  /** `POST /notifications/batches` — submit a batch of notifications in one call. */
  async bulkSubmit(request) {
    return await this.create(request);
  }
  /** `GET /notifications/batches/{id}` — fetch a single batch (with its per-status counts). */
  async getById(id) {
    return await this.fetch(id);
  }
  /** `GET /notifications/batches?source=&sourceKey=` — find batches by their submitting source. */
  async findBySource(source, sourceKey) {
    const query = { source };
    if (sourceKey !== void 0) query.sourceKey = sourceKey;
    return await this.get(query);
  }
  /**
   * `POST /notifications/batches/query` — run an MQL query over batches and get back a page of
   * results (`values` plus `totalCount`/`skip`/`take`).
   *
   * Pass the request-broker `metadata`; it's compiled to an MQL string via {@link MqlRequestBuilder}
   * before invoking the API. The FETCH name is the model's .NET type name (`NotificationBatchModel`).
   */
  async queryMql(metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("NotificationBatchModel") : "FETCH NotificationBatchModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query
    });
  }
}
class NotificationOptOutsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "notifications/opt-outs");
  }
  /** `POST /notifications/opt-outs` — opt an address out of a channel. */
  async optOut(request) {
    return await this.create(request);
  }
  /**
   * `DELETE /notifications/opt-outs?channel=&address=` — remove an opt-out (opt the address back in).
   * Returns 204 No Content.
   */
  async optIn(channel, address) {
    await this.httpDELETE({
      url: this.appendUrl(""),
      query: { channel: String(channel), address }
    });
  }
  /**
   * `GET /notifications/opt-outs?channel=&address=` — find an address's opt-out for a channel.
   * Resolves to the opt-out record, or `null` when the address is not opted out.
   */
  async find(channel, address) {
    return await this.get({ channel: String(channel), address });
  }
  /**
   * `POST /notifications/opt-outs/query` — run an MQL query over opt-outs and get back a page of
   * results (`values` plus `totalCount`/`skip`/`take`).
   *
   * Pass the request-broker `metadata` (the filters/sorting/paging the frontend collected); it's
   * compiled to an MQL string via {@link MqlRequestBuilder} before invoking the API. The FETCH
   * name is the model's .NET type name (`OptOutModel`) — that's what the MQL engine registers,
   * not the `[SchemaId]` used for the OpenAPI/type name.
   */
  async queryMql(metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("OptOutModel") : "FETCH OptOutModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query
    });
  }
}
class DialersClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "notifications/dialers");
  }
  /** `GET /notifications/dialers` */
  async getDialers() {
    return await this.httpGET({ url: this.appendUrl("/") });
  }
  /** `GET /notifications/dialers/{dialerId}/campaigns` */
  async getCampaigns(dialerId) {
    return await this.httpGET({
      url: this.appendUrl(`/${dialerId}/campaigns`)
    });
  }
  /** `GET /notifications/dialers/{dialerId}/campaigns/{id}` */
  async getCampaign(dialerId, id) {
    return await this.httpGET({
      url: this.appendUrl(`/${dialerId}/campaigns/${id}`)
    });
  }
  /**
   * `POST /notifications/dialers/{dialerId}/campaigns` — returns the saved campaign with its
   * server-generated `uniqueKey`.
   */
  async createCampaign(dialerId, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${dialerId}/campaigns`),
      body: request
    });
  }
  /** `PUT /notifications/dialers/{dialerId}/campaigns/{id}` (the `uniqueKey` is never changed). */
  async updateCampaign(dialerId, id, request) {
    return await this.httpPUT({
      url: this.appendUrl(`/${dialerId}/campaigns/${id}`),
      body: request
    });
  }
  /** `DELETE /notifications/dialers/{dialerId}/campaigns/{id}` → 204 No Content. */
  async deleteCampaign(dialerId, id) {
    await this.httpDELETE({ url: this.appendUrl(`/${dialerId}/campaigns/${id}`) });
  }
}
class TeamsDestinationsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "notifications/teams-destinations");
  }
  /** `GET /notifications/teams-destinations` */
  async getDestinations() {
    return await this.httpGET({ url: this.appendUrl("/") });
  }
  /** `POST /notifications/teams-destinations` — returns the saved destination with its `uniqueKey`. */
  async createDestination(request) {
    return await this.httpPOST({
      url: this.appendUrl("/"),
      body: request
    });
  }
  /** `PUT /notifications/teams-destinations/{id}` — without `webhookUrl` the stored URL is kept. */
  async updateDestination(id, request) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}`),
      body: request
    });
  }
  /** `DELETE /notifications/teams-destinations/{id}` → 204 No Content. */
  async deleteDestination(id) {
    await this.httpDELETE({ url: this.appendUrl(`/${id}`) });
  }
  /**
   * `POST /notifications/teams-destinations/{id}/test` → 204 when the flow accepted the test card,
   * 400 with the reason when it did not.
   */
  async sendTest(id, request = {}) {
    await this.httpPOST({ url: this.appendUrl(`/${id}/test`), body: request });
  }
}
const NotificationsConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "NotificationsAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "NotificationsAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "NotificationsAPI"
    }
  }
});
const notificationsPageKey = (queryMql, accountId) => accountId != null ? `notifications/account/${accountId}` : queryMql.action;
const NotificationsService = reactive.service(
  "notifications",
  NotificationsClient
)((Service) => new Service(NotificationsConfig.current.api.host));
NotificationsService.queryMql.latest(([, actionKey]) => actionKey);
const pageKeysOf = (notification) => notification.accountId != null ? [
  notificationsPageKey(NotificationsService.queryMql),
  notificationsPageKey(NotificationsService.queryMql, notification.accountId)
] : [notificationsPageKey(NotificationsService.queryMql)];
NotificationsService.reactor("notificationsReactor", {
  success: (response, _, db2) => {
    const { source, args, payload } = response;
    switch (source) {
      case NotificationsService.queryMql: {
        const [, actionKey] = args ?? [];
        const pageKey = actionKey ?? source.action;
        RequestMetadata.setCount(pageKey, payload.totalCount ?? 0);
        return replaceRows(
          db2,
          db2.notificationsPages,
          payload.values?.map((row) => ({ ...row, pageKey })),
          { where: (pages) => pages.where("pageKey").equals(pageKey) }
        );
      }
      case NotificationsService.getById:
        return db2.notifications.put(payload);
      // Into the lists it belongs to as well, so they show the new notification without a query.
      case NotificationsService.submit:
        return Promise.all([
          db2.notifications.put(payload),
          db2.notificationsPages.bulkPut(
            pageKeysOf(payload).map((pageKey) => ({ ...payload, pageKey }))
          )
        ]);
      case NotificationsService.cancel:
        return Promise.all([
          db2.notifications.put(payload),
          db2.notificationsPages.where("id").equals(payload.id).modify((row) => Object.assign(row, payload))
        ]);
      case NotificationsService.getHistory:
        return db2.history.bulkPut(payload ?? []);
      case NotificationsService.queryByBatch:
        return replaceRows(db2, db2.batchNotifications, payload.values);
    }
  },
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const OptOutsService = reactive.service("optOuts", NotificationOptOutsClient)(
  (Service) => new Service(NotificationsConfig.current.api.host)
);
OptOutsService.queryMql.latest();
OptOutsService.reactor("optOutsReactor", {
  success: (response, _, db2) => {
    const { source, payload, args } = response;
    switch (source) {
      case OptOutsService.queryMql:
        RequestMetadata.setCount(source.action, payload.totalCount ?? 0);
        return replaceRows(db2, db2.optOuts, payload.values);
      case OptOutsService.optOut:
        return db2.optOuts.put(payload);
      case OptOutsService.optIn: {
        const [channel, address] = args;
        return db2.optOuts.filter((row) => row.channel === channel && row.address === address).delete();
      }
    }
  },
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const BatchesService = reactive.service("batches", NotificationBatchesClient)(
  (Service) => new Service(NotificationsConfig.current.api.host)
);
BatchesService.queryMql.latest();
BatchesService.reactor("batchesReactor", {
  success: (response, _, db2) => {
    const { source, payload } = response;
    switch (source) {
      case BatchesService.queryMql:
        RequestMetadata.setCount(source.action, payload.totalCount ?? 0);
        return replaceRows(db2, db2.batchesPage, payload.values);
      case BatchesService.getById:
        return db2.batches.put(payload);
      // Into the grid's page too, so the list shows the new batch without a query.
      case BatchesService.bulkSubmit:
        return Promise.all([db2.batches.put(payload), db2.batchesPage.put(payload)]);
    }
  },
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const DialersService = reactive.service("dialers", DialersClient)(
  (Service) => new Service(NotificationsConfig.current.api.host)
);
DialersService.reactor("dialersReactor", {
  success: (response, _, db2) => {
    const { source, payload, args } = response;
    switch (source) {
      case DialersService.getDialers:
        return replaceRows(db2, db2.dialers, payload);
      case DialersService.getCampaigns: {
        const [dialerId] = args;
        return replaceRows(db2, db2.campaigns, payload, {
          where: (campaigns) => campaigns.where("dialerId").equals(dialerId)
        });
      }
      case DialersService.getCampaign:
      case DialersService.createCampaign:
      case DialersService.updateCampaign:
        return db2.campaigns.put(payload);
      case DialersService.deleteCampaign: {
        const [, id] = args;
        return db2.campaigns.delete(id);
      }
    }
  },
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const TeamsDestinationsService = reactive.service(
  "teamsDestinations",
  TeamsDestinationsClient
)((Service) => new Service(NotificationsConfig.current.api.host));
TeamsDestinationsService.reactor("teamsDestinationsReactor", {
  success: (response, _, db2) => {
    const { source, payload, args } = response;
    switch (source) {
      case TeamsDestinationsService.getDestinations:
        return replaceRows(db2, db2.teamsDestinations, payload);
      // Making one destination the default clears the other default of its kind on the server, so
      // reload the list rather than putting just the saved row.
      case TeamsDestinationsService.createDestination:
      case TeamsDestinationsService.updateDestination:
        return payload?.isDefault ? TeamsDestinationsService.getDestinations() : db2.teamsDestinations.put(payload);
      case TeamsDestinationsService.deleteDestination: {
        const [id] = args;
        return db2.teamsDestinations.delete(id);
      }
      case TeamsDestinationsService.sendTest:
        return appRuntime.services.alerts.success(
          "The flow accepted the test card. Check the channel or chat to see it arrive."
        );
    }
  },
  error: (response) => appRuntime.services.alerts.error(
    response.payload?.message ?? (typeof response.payload === "string" ? response.payload : "Error")
  )
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const db = new DexieDatabaseProvider([dexieRequestBrokerAddon]);
const ReactiveConfig = {
  db: {
    name: "livefree.notifications.db",
    provider: db,
    init: (db2) => {
      db2.version(1).stores(storeFormat(_storeV1));
      db2.version(2).stores(storeFormat(_storeV2));
      db2.version(3).stores(storeFormat(_storeV3));
      db2.version(4).stores(storeFormat(_storeV4));
      db2.version(5).stores(storeFormat(_storeV5));
      db2.version(6).stores(storeFormat(_storeV6));
      db2.version(7).stores(storeFormat(_storeV7));
      db2.version(8).stores(storeFormat(_storeV8));
      db2.version(9).stores(storeFormat(_storeV9));
      db2.version(10).stores({ ...storeFormat(_store), notificationsPage: null });
    }
  }
};
const _storeV1 = {
  notifications: ["&id"]
};
const _storeV2 = {
  notifications: ["&id"],
  history: ["&id", "notificationId"]
};
const _storeV3 = {
  notifications: ["&id"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"]
};
const _storeV4 = {
  notifications: ["&id"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"],
  batches: ["&id"]
};
const _storeV5 = {
  notifications: ["&id"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"],
  batches: ["&id"],
  batchNotifications: ["&id", "createdOnUtc"]
};
const _storeV6 = {
  notifications: ["&id"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"],
  batches: ["&id"],
  batchNotifications: ["&id", "createdOnUtc"],
  dialers: ["&id"],
  campaigns: ["&id", "dialerId"]
};
const _storeV7 = {
  notifications: ["&id", "accountId"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"],
  batches: ["&id"],
  batchNotifications: ["&id", "createdOnUtc"],
  dialers: ["&id"],
  campaigns: ["&id", "dialerId"]
};
const _storeV8 = {
  notifications: ["&id", "accountId"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"],
  batches: ["&id"],
  batchNotifications: ["&id", "createdOnUtc"],
  dialers: ["&id"],
  campaigns: ["&id", "dialerId"],
  teamsDestinations: ["&id"]
};
const _storeV9 = {
  notifications: ["&id", "accountId"],
  notificationsPage: ["&id", "accountId"],
  history: ["&id", "notificationId"],
  optOuts: ["&id"],
  batches: ["&id"],
  batchesPage: ["&id"],
  batchNotifications: ["&id", "createdOnUtc"],
  dialers: ["&id"],
  campaigns: ["&id", "dialerId"],
  teamsDestinations: ["&id"]
};
const _store = {
  // Notifications read one at a time (getById, submit, cancel).
  notifications: ["&id", "accountId"],
  // The notifications grids' pages (queryMql results), apart from `notifications` so replacing a
  // page cannot erase a row read on its own. One page per list scope: each row carries its list's
  // request-broker key as `pageKey` (see notificationsPageKey), so the same notification can be on
  // the all-notifications page and on its account's. Indexed by `id` so a change reaches every
  // copy. Added in v10, in place of v9's `notificationsPage`.
  notificationsPages: ["[pageKey+id]", "pageKey", "id"],
  // History rows keyed by their own `id`, indexed by `notificationId` so the history controller
  // can scope a query to the selected notification.
  history: ["&id", "notificationId"],
  // Opt-outs list (queryMql results).
  optOuts: ["&id"],
  // Batches read one at a time; the batch detail screen reads its batch from here.
  batches: ["&id"],
  // The batches grid's page (queryMql results), apart from `batches` so replacing the page cannot
  // erase the batch the detail screen is showing. Added in v9.
  batchesPage: ["&id"],
  // Notifications belonging to the batch currently open on the detail screen.
  batchNotifications: ["&id", "createdOnUtc"],
  // Auto-dialers (lookup) and their campaigns (indexed by dialerId). Added in v6 — bump the
  // version so already-provisioned databases get the stores on upgrade.
  dialers: ["&id"],
  campaigns: ["&id", "dialerId"],
  // Teams destinations (Workflows webhooks). Added in v8.
  teamsDestinations: ["&id"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const toOptions = (names) => names.map((name) => ({ value: name, label: name }));
const createNotificationFilters = ({ accountId } = {}) => new Filters([
  new FilterSelect({
    name: "channelName",
    title: "Channel",
    loader: () => Promise.resolve(toOptions(Object.keys(NotificationChannel)))
  }),
  new FilterSelect({
    name: "statusName",
    title: "Status",
    loader: () => Promise.resolve(toOptions(Object.keys(NotificationStatusType)))
  }),
  new FilterInput({ name: "toAddress", title: "To Address" }),
  new FilterInput({ name: "toName", title: "To Name" }),
  new FilterInput({ name: "subject", title: "Subject" }),
  new FilterDateRange({ name: "scheduledForUtc", title: "Scheduled For" }),
  accountId != null ? new FilterConstant({ name: "accountId", title: "Account", defaultValue: accountId }) : new FilterNumericInput({ name: "accountId", title: "Account ID", numberType: "integer" }),
  new FilterDateRange({ name: "sentOnUtc", title: "Sent On" })
]);
const createNotificationSorting = () => new Sorting(
  [
    { value: "channelName", title: "Channel" },
    { value: "statusName", title: "Status" },
    { value: "toAddress", title: "To Address" },
    { value: "toName", title: "To Name" },
    { value: "subject", title: "Subject" },
    { value: "scheduledForUtc", title: "Scheduled For" },
    { value: "accountId", title: "Account ID" },
    { value: "sentOnUtc", title: "Sent On" },
    { value: "createdOnUtc", title: "Created On" }
  ],
  "createdOnUtc",
  "desc"
);
const columnDefs$2 = [
  {
    propertyName: "channelName",
    name: "Channel",
    isSortable: true,
    isResizable: true,
    minWidth: 120
  },
  {
    propertyName: "statusName",
    name: "Status",
    isSortable: true,
    isResizable: true,
    minWidth: 130
  },
  {
    propertyName: "toAddress",
    name: "To Address",
    isSortable: true,
    isResizable: true,
    minWidth: 200
  },
  { propertyName: "toName", name: "To Name", isSortable: true, isResizable: true, minWidth: 160 },
  { propertyName: "subject", name: "Subject", isSortable: true, isResizable: true, minWidth: 220 },
  {
    propertyName: "scheduledForUtc",
    name: "Scheduled For",
    isSortable: true,
    isResizable: true,
    minWidth: 170
  },
  {
    propertyName: "accountId",
    name: "Account",
    isSortable: true,
    isResizable: true,
    minWidth: 130
  },
  {
    propertyName: "sentOnUtc",
    name: "Sent On",
    isSortable: true,
    isResizable: true,
    minWidth: 170
  }
];
const statusColor$3 = (status) => {
  switch (status) {
    case "Sent":
    case "Delivered":
      return "success";
    case "Failed":
    case "Bounced":
      return "danger";
    case "Suppressed":
    case "Canceled":
      return "default";
    default:
      return "accent";
  }
};
const NotificationsTable = ({
  notifications = [],
  showAccount = true,
  onSelect,
  onCreate
}) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "channelName":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: cellValue });
      case "statusName":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: statusColor$3(cellValue), size: "sm", children: cellValue });
      case "scheduledForUtc":
      case "sentOnUtc":
        return cellValue ? parseAndFormatDateTime(cellValue) : "";
      default:
        return cellValue ?? "";
    }
  }, []);
  const columns2 = useMemo(
    () => columnDefs$2.filter((column) => showAccount || column.propertyName !== "accountId").map((column) => ({
      id: column.propertyName,
      header: column.name,
      isRowHeader: column.propertyName === "toAddress",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [showAccount, renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Notifications list table",
      hideChips: true,
      columns: columns2,
      data: notifications,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      createLabel: "Send Message",
      onCreate,
      onRowAction: (key) => {
        const selected = notifications.find((item) => item.id === key);
        if (selected && onSelect) onSelect(selected);
      },
      topContent: /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-4", children: [
        /* @__PURE__ */ jsx(HorizontalFilters, { scope: "custom", classNames: { base: "grow-1" } }),
        /* @__PURE__ */ jsx(HorizontalChips, { scope: "global" })
      ] })
    }
  );
};
const NotificationsListController = reactive.component(
  ({ services, data, monitors, accountId, onSelect, onCreate }) => {
    const { notifications } = data;
    const actionKey = notificationsPageKey(services.notifications.queryMql, accountId);
    const filters2 = useMemo(() => createNotificationFilters({ accountId }), [accountId]);
    const sorting2 = useMemo(() => createNotificationSorting(), []);
    const handleExecute = useCallback(
      (action, metadata) => {
        action(metadata, actionKey);
      },
      [actionKey]
    );
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey,
        action: services.notifications.queryMql,
        filters: filters2,
        sorting: sorting2,
        paging: new Paging(50),
        isExecuting: monitors.queryMql.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          NotificationsTable,
          {
            notifications,
            showAccount: accountId == null,
            onSelect: (item) => onSelect && onSelect(item),
            onCreate
          }
        )
      },
      actionKey
    );
  },
  {
    name: "NotificationsListController",
    queries: ({ accountId }, services) => [
      {
        name: "notifications",
        defaultValue: [],
        execute: (db2) => {
          const pageKey = notificationsPageKey(services.notifications.queryMql, accountId);
          return db2.withRequestBroker(
            pageKey,
            () => db2.notificationsPages.where("pageKey").equals(pageKey),
            { sorting: true }
          );
        },
        deps: [accountId]
      }
    ],
    monitors: (services) => [services.notifications.queryMql]
  }
);
const statusTone = (name) => {
  switch (name) {
    case "Sent":
    case "Delivered":
      return "success";
    case "Failed":
    case "Bounced":
      return "danger";
    case "Suppressed":
    case "Canceled":
      return "muted";
    default:
      return "default";
  }
};
const NotificationHistoryTimeline = ({ history = [] }) => /* @__PURE__ */ jsx(Timeline, { density: "compact", size: "sm", children: history.map((entry) => /* @__PURE__ */ jsxs(
  Timeline.Item,
  {
    align: entry.detail ? "start" : "center",
    status: statusTone(entry.statusName),
    children: [
      /* @__PURE__ */ jsx(Timeline.Marker, {}),
      /* @__PURE__ */ jsxs(Timeline.Content, { className: "gap-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3", children: [
          /* @__PURE__ */ jsx("h3", { className: "text-foreground m-0 text-xs font-medium leading-tight", children: entry.statusName }),
          /* @__PURE__ */ jsx("time", { className: "text-muted shrink-0 text-xs leading-tight", children: parseAndFormatDateTime(entry.createdOnUtc) })
        ] }),
        entry.detail && /* @__PURE__ */ jsx("p", { className: "text-muted m-0 text-xs leading-snug whitespace-pre-wrap", children: entry.detail }),
        entry.createdBy && /* @__PURE__ */ jsxs("span", { className: "text-muted text-[11px] leading-4", children: [
          "by ",
          entry.createdBy
        ] })
      ] })
    ]
  },
  entry.id
)) });
const Detail = ({ label, value }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
  /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: label }),
  /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm break-words", children: value })
] });
const NotificationContent = ({ notification, showRecipient = true }) => {
  if (!notification) return null;
  const { channelName, toAddress, toName, subject, body, isHtml } = notification;
  const recipient = [toName, toAddress].filter(Boolean).join(" · ");
  return /* @__PURE__ */ jsxs("div", { className: "bg-surface-secondary flex flex-col gap-3 rounded-xl p-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-x-6 gap-y-2", children: [
      channelName && /* @__PURE__ */ jsx(Detail, { label: "Channel", value: channelName }),
      showRecipient && recipient && /* @__PURE__ */ jsx(Detail, { label: "To", value: recipient })
    ] }),
    subject && /* @__PURE__ */ jsx(Detail, { label: "Subject", value: subject }),
    body && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Content" }),
      isHtml ? /* @__PURE__ */ jsx(
        "div",
        {
          className: "text-foreground max-w-none text-sm [&_a]:underline [&_p]:my-1",
          dangerouslySetInnerHTML: { __html: body }
        }
      ) : /* @__PURE__ */ jsx("p", { className: "text-foreground text-sm whitespace-pre-wrap", children: body })
    ] })
  ] });
};
const NotificationHistoryController = reactive.component(
  ({ data, monitors, notification }) => {
    const { history = [] } = data;
    const loading = monitors.getHistory.executing;
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsx(NotificationContent, { notification }),
      loading && history.length === 0 ? /* @__PURE__ */ jsx("div", { className: "flex justify-center py-8", children: /* @__PURE__ */ jsx(Spinner, {}) }) : history.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-muted py-4 text-center text-sm", children: "No history for this message." }) : /* @__PURE__ */ jsx(NotificationHistoryTimeline, { history })
    ] });
  },
  {
    name: "NotificationHistoryController",
    sync: (services, { notificationId }) => [
      {
        execute: () => notificationId != null ? services.notifications.getHistory(notificationId) : Promise.resolve(),
        deps: [notificationId]
      }
    ],
    queries: ({ notificationId }) => [
      {
        name: "history",
        defaultValue: [],
        execute: (db2) => notificationId != null ? db2.history.where("notificationId").equals(notificationId).sortBy("createdOnUtc").then((items) => items.reverse()) : [],
        deps: [notificationId]
      }
    ],
    monitors: (services) => [services.notifications.getHistory]
  }
);
NotificationHistoryController.Drawer = withDrawerController(NotificationHistoryController);
const INPUT_CLASS$6 = "bg-surface-secondary text-foreground placeholder:text-muted w-full resize-y rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const SmsContentInput = ({
  value,
  onChange,
  columns: columns2 = [],
  sampleRow,
  showSegments = true,
  noun = "SMS"
}) => {
  const ref = useRef(null);
  const insert = (token) => {
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    columns2.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1", children: columns2.map((column) => /* @__PURE__ */ jsx(Button, { size: "sm", variant: "secondary", onPress: () => insert(`{{${column}}}`), children: `{{${column}}}` }, column)) }),
    /* @__PURE__ */ jsx(
      "textarea",
      {
        ref,
        rows: 4,
        value,
        onChange: (event) => onChange(event.target.value),
        placeholder: columns2.length > 0 ? `${noun} message — use the buttons above to insert {{placeholders}}` : `${noun} message`,
        className: INPUT_CLASS$6
      }
    ),
    showSegments && /* @__PURE__ */ jsx(SmsSegments, { text: value, placeholderValues: sampleRow })
  ] });
};
const INPUT_CLASS$5 = "bg-surface-secondary text-foreground placeholder:text-muted w-full rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const PlaceholderButtons = ({ columns: columns2 }) => {
  const { editor } = useRichTextEditor();
  if (!columns2.length) return null;
  return /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1 px-2 pb-2", children: columns2.map((column) => /* @__PURE__ */ jsx(
    Button,
    {
      size: "sm",
      variant: "secondary",
      onPress: () => editor?.chain().focus().insertContent(`{{${column}}}`).run(),
      children: `{{${column}}}`
    },
    column
  )) });
};
const EmailContentInput = ({ subject, onSubjectChange, onBodyChange, columns: columns2 = [] }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsx("label", { className: "text-foreground text-sm font-medium", children: "Subject" }),
    /* @__PURE__ */ jsx(
      "input",
      {
        value: subject,
        onChange: (event) => onSubjectChange(event.target.value),
        placeholder: columns2.length > 0 ? "Email subject (supports {{placeholders}})" : "Email subject",
        className: INPUT_CLASS$5
      }
    )
  ] }),
  /* @__PURE__ */ jsx(
    RichTextEditor,
    {
      placeholder: "Compose the email…",
      onValueChange: (_json, details) => onBodyChange(details.html),
      children: /* @__PURE__ */ jsxs(RichTextEditor.Shell, { className: "bg-surface-secondary", children: [
        /* @__PURE__ */ jsx(RichTextEditor.Toolbar, { children: /* @__PURE__ */ jsxs(RichTextEditor.ToolbarGroup, { children: [
          /* @__PURE__ */ jsx(RichTextEditor.ToggleButton, { command: "bold", tooltip: "Bold", children: /* @__PURE__ */ jsx("i", { className: "ri-bold" }) }),
          /* @__PURE__ */ jsx(RichTextEditor.ToggleButton, { command: "italic", tooltip: "Italic", children: /* @__PURE__ */ jsx("i", { className: "ri-italic" }) }),
          /* @__PURE__ */ jsx(RichTextEditor.ToggleButton, { command: "underline", tooltip: "Underline", children: /* @__PURE__ */ jsx("i", { className: "ri-underline" }) }),
          /* @__PURE__ */ jsx(RichTextEditor.ToggleButton, { command: "bulletList", tooltip: "Bulleted list", children: /* @__PURE__ */ jsx("i", { className: "ri-list-unordered" }) }),
          /* @__PURE__ */ jsx(RichTextEditor.ToggleButton, { command: "orderedList", tooltip: "Numbered list", children: /* @__PURE__ */ jsx("i", { className: "ri-list-ordered" }) })
        ] }) }),
        /* @__PURE__ */ jsx(RichTextEditor.Content, {}),
        /* @__PURE__ */ jsx(PlaceholderButtons, { columns: columns2 }),
        /* @__PURE__ */ jsxs(RichTextEditor.Footer, { children: [
          columns2.length > 0 && /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Insert a column with the buttons above" }),
          /* @__PURE__ */ jsx(RichTextEditor.CharacterCount, {})
        ] })
      ] })
    }
  )
] });
const INPUT_CLASS$4 = "bg-surface-secondary text-foreground w-full max-w-40 rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const DEFAULT_VOICE_REPEAT = 3;
const REPEAT_OPTIONS = [2, 3, 4, 5];
const resolveVoiceRepeatCount = ({ enabled, count } = {}) => enabled ? count ?? DEFAULT_VOICE_REPEAT : null;
const VoiceRepeatField = ({ value, onChange }) => {
  const enabled = value?.enabled ?? false;
  const count = value?.count ?? DEFAULT_VOICE_REPEAT;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs("label", { className: "text-foreground flex items-center gap-2 text-sm font-medium", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          checked: enabled,
          onChange: (event) => onChange({ enabled: event.target.checked, count })
        }
      ),
      "Repeat the voice message"
    ] }),
    enabled && /* @__PURE__ */ jsx(
      "select",
      {
        "aria-label": "Repeat count",
        value: count,
        onChange: (event) => onChange({ enabled, count: Number(event.target.value) }),
        className: INPUT_CLASS$4,
        children: REPEAT_OPTIONS.map((option) => /* @__PURE__ */ jsxs("option", { value: option, children: [
          option,
          " times"
        ] }, option))
      }
    )
  ] });
};
const toKey$1 = (value) => value != null ? String(value) : null;
const fromKey$1 = (key) => key != null && key !== "" ? Number(key) : null;
const DialerCampaignPicker = reactive.component(
  ({ data, value, onChange }) => {
    const { dialers = [], campaigns = [] } = data;
    const dialerId = value?.dialerId ?? null;
    const campaignId = value?.campaignId ?? null;
    const selectDialer = (key) => {
      onChange({ dialerId: fromKey$1(key), campaignId: null, campaignKey: null });
    };
    const selectCampaign = (key) => {
      const campaign = campaigns.find((item) => item.id === fromKey$1(key));
      onChange({
        dialerId,
        campaignId: campaign?.id ?? null,
        campaignKey: campaign?.uniqueKey ?? null
      });
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
          "Dialer ",
          /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsxs(Select, { "aria-label": "Dialer", value: toKey$1(dialerId), onChange: selectDialer, children: [
          /* @__PURE__ */ jsxs(Select.Trigger, { className: "bg-surface-secondary", children: [
            /* @__PURE__ */ jsx(Select.Value, { children: ({ state }) => {
              const selected = dialers.find(
                (item) => String(item.id) === String(state.selectedItems?.[0]?.key)
              );
              return selected ? selected.name : "Select a dialer";
            } }),
            /* @__PURE__ */ jsx(Select.Indicator, {})
          ] }),
          /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { children: dialers.map((dialer) => /* @__PURE__ */ jsxs(ListBox.Item, { id: String(dialer.id), textValue: dialer.name, children: [
            dialer.name,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }, dialer.id)) }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
          "Campaign ",
          /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsxs(
          Select,
          {
            "aria-label": "Campaign",
            isDisabled: dialerId == null,
            value: toKey$1(campaignId),
            onChange: selectCampaign,
            children: [
              /* @__PURE__ */ jsxs(Select.Trigger, { className: "bg-surface-secondary", children: [
                /* @__PURE__ */ jsx(Select.Value, { children: ({ state }) => {
                  const selected = campaigns.find(
                    (item) => String(item.id) === String(state.selectedItems?.[0]?.key)
                  );
                  if (selected) return selected.name;
                  return dialerId == null ? "Select a dialer first" : "Select a campaign";
                } }),
                /* @__PURE__ */ jsx(Select.Indicator, {})
              ] }),
              /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { children: campaigns.map((campaign) => /* @__PURE__ */ jsxs(ListBox.Item, { id: String(campaign.id), textValue: campaign.name, children: [
                /* @__PURE__ */ jsx(Label, { children: campaign.name }),
                campaign.description && /* @__PURE__ */ jsx(Description, { children: campaign.description }),
                /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
              ] }, campaign.id)) }) })
            ]
          }
        )
      ] })
    ] });
  },
  {
    name: "DialerCampaignPicker",
    sync: (services, { value }) => [
      { execute: () => services.dialers.getDialers(), deps: [] },
      {
        execute: () => value?.dialerId != null ? services.dialers.getCampaigns(value.dialerId) : Promise.resolve(),
        deps: [value?.dialerId]
      }
    ],
    queries: ({ value }) => [
      { name: "dialers", defaultValue: [], execute: (db2) => db2.dialers.toArray(), deps: [] },
      {
        name: "campaigns",
        defaultValue: [],
        execute: (db2) => value?.dialerId != null ? db2.campaigns.where("dialerId").equals(value.dialerId).toArray() : [],
        deps: [value?.dialerId]
      }
    ]
  }
);
const INPUT_CLASS$3 = "bg-surface-secondary text-foreground placeholder:text-muted w-full rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const CUSTOMER_SEARCH_COMBO_BOX = "accounts.customer-search-combo-box";
const CUSTOMER_PHONE_PICKER = "accounts.customer-phone-picker";
const CUSTOMER_EMAIL_BUTTON = "accounts.customer-email-button";
const EMPTY_FORM = {
  accountId: null,
  channel: "sms",
  toAddress: "",
  toName: "",
  smsBody: "",
  voiceRepeat: { enabled: false, count: 3 },
  dialer: { dialerId: null, campaignId: null, campaignKey: null },
  emailSubject: "",
  emailBodyHtml: "",
  scheduleEnabled: false,
  scheduledLocal: ""
};
const isValid = (form, isAccountRequired) => {
  if (isAccountRequired && form.accountId == null) return false;
  if (!form.toAddress.trim()) return false;
  if ((form.channel === "sms" || form.channel === "voice") && !form.smsBody.trim()) return false;
  if (form.channel === "email" && (!form.emailSubject.trim() || !form.emailBodyHtml.trim())) {
    return false;
  }
  if (form.channel === "dialer" && !form.dialer.campaignKey) return false;
  if (form.scheduleEnabled) {
    if (!form.scheduledLocal) return false;
    if (new Date(form.scheduledLocal).getTime() <= Date.now()) return false;
  }
  return true;
};
const buildRequest = (form) => {
  const base = {
    toAddress: form.toAddress.trim(),
    toName: form.toName.trim() || void 0,
    accountId: form.accountId ?? void 0,
    scheduledForUtc: form.scheduleEnabled && form.scheduledLocal ? toISO(form.scheduledLocal) : void 0
  };
  if (form.channel === "email") {
    return {
      ...base,
      channel: NotificationChannel.Email,
      subject: form.emailSubject.trim(),
      body: form.emailBodyHtml,
      isHtml: true
    };
  }
  if (form.channel === "dialer") {
    return {
      ...base,
      channel: NotificationChannel.Dialer,
      campaignKey: form.dialer.campaignKey,
      isHtml: false
    };
  }
  return {
    ...base,
    channel: form.channel === "voice" ? NotificationChannel.Voice : NotificationChannel.Sms,
    body: form.smsBody,
    isHtml: false,
    voiceRepeatCount: form.channel === "voice" ? resolveVoiceRepeatCount(form.voiceRepeat) : null
  };
};
const NotificationSubmitController = reactive.component(
  ({ services, monitors, accountId, onClose }) => {
    const runtime = useRuntimeProvider();
    const isAccountRequired = !runtime.user.hasPolicy(
      runtime.config.api.apiKey,
      "Notifications",
      "Send",
      "All"
    );
    const CustomerSearchComboBox = useComponent(CUSTOMER_SEARCH_COMBO_BOX);
    const CustomerPhonePicker = useComponent(CUSTOMER_PHONE_PICKER);
    const CustomerEmailButton = useComponent(CUSTOMER_EMAIL_BUTTON);
    const isScoped = accountId != null;
    const emptyForm = useMemo(
      () => isScoped ? { ...EMPTY_FORM, accountId: Number(accountId) } : EMPTY_FORM,
      [isScoped, accountId]
    );
    const [form, setForm] = useState(emptyForm);
    const submitting = monitors.submit.executing;
    const isEmail = form.channel === "email";
    const hasCustomer = form.accountId != null;
    const valid = useMemo(() => isValid(form, isAccountRequired), [form, isAccountRequired]);
    useSuccessReactor(services.notifications.submit, () => {
      setForm(emptyForm);
      onClose?.();
    });
    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const selectChannel = (next) => update(
      next === "email" === (form.channel === "email") ? { channel: next } : { channel: next, toAddress: "" }
    );
    const handleSubmit = () => {
      if (!valid) return;
      services.notifications.submit(buildRequest(form));
    };
    const pickAddress = (toAddress) => update({ toAddress });
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-foreground text-sm font-medium", children: [
          "Customer ",
          isAccountRequired && /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsx(
          CustomerSearchComboBox,
          {
            "aria-label": "Customer",
            fullWidth: true,
            isRequired: isAccountRequired,
            isDisabled: isScoped,
            isClearable: !isScoped,
            defaultValue: form.accountId,
            onSelect: (selected) => update({ accountId: selected })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm font-medium", children: "Channel" }),
        /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: form.channel === "sms" ? "primary" : "secondary",
              onPress: () => selectChannel("sms"),
              children: "SMS"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: form.channel === "voice" ? "primary" : "secondary",
              onPress: () => selectChannel("voice"),
              children: "Voice"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: form.channel === "dialer" ? "primary" : "secondary",
              onPress: () => selectChannel("dialer"),
              children: "Dialer"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: form.channel === "email" ? "primary" : "secondary",
              onPress: () => selectChannel("email"),
              children: "Email"
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
            isEmail ? "Email address" : "Phone number",
            " ",
            /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
          ] }),
          hasCustomer && (isEmail ? /* @__PURE__ */ jsx(CustomerEmailButton, { accountId: form.accountId, onSelect: pickAddress }) : /* @__PURE__ */ jsx(CustomerPhonePicker, { accountId: form.accountId, onSelect: pickAddress }))
        ] }),
        isEmail ? /* @__PURE__ */ jsx(
          "input",
          {
            type: "email",
            value: form.toAddress,
            onChange: (event) => update({ toAddress: event.target.value }),
            placeholder: "name@example.com",
            className: INPUT_CLASS$3
          }
        ) : /* @__PURE__ */ jsx(PhoneInput, { value: form.toAddress, onChange: (toAddress) => update({ toAddress }) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("label", { className: "text-foreground text-sm font-medium", children: "Recipient name" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            value: form.toName,
            onChange: (event) => update({ toName: event.target.value }),
            placeholder: "Optional",
            className: INPUT_CLASS$3
          }
        )
      ] }),
      isEmail ? /* @__PURE__ */ jsx(
        EmailContentInput,
        {
          subject: form.emailSubject,
          onSubjectChange: (emailSubject) => update({ emailSubject }),
          onBodyChange: (emailBodyHtml) => update({ emailBodyHtml }),
          columns: []
        }
      ) : form.channel === "dialer" ? /* @__PURE__ */ jsx(DialerCampaignPicker, { value: form.dialer, onChange: (dialer) => update({ dialer }) }) : form.channel === "voice" ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
          /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
            "Message ",
            /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
          ] }),
          /* @__PURE__ */ jsx(
            "textarea",
            {
              value: form.smsBody,
              onChange: (event) => update({ smsBody: event.target.value }),
              rows: 4,
              placeholder: "Message to be read aloud (converted to speech)",
              className: `${INPUT_CLASS$3} resize-y`
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          VoiceRepeatField,
          {
            value: form.voiceRepeat,
            onChange: (voiceRepeat) => update({ voiceRepeat })
          }
        )
      ] }) : /* @__PURE__ */ jsx(
        SmsContentInput,
        {
          value: form.smsBody,
          onChange: (smsBody) => update({ smsBody }),
          columns: []
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground flex items-center gap-2 text-sm font-medium", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked: form.scheduleEnabled,
              onChange: (event) => update({ scheduleEnabled: event.target.checked })
            }
          ),
          "Schedule for later"
        ] }),
        form.scheduleEnabled && /* @__PURE__ */ jsx(
          "input",
          {
            type: "datetime-local",
            value: form.scheduledLocal,
            onChange: (event) => update({ scheduledLocal: event.target.value }),
            className: INPUT_CLASS$3
          }
        )
      ] }),
      /* @__PURE__ */ jsxs(DrawerActions, { children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", onPress: () => onClose?.(), children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            size: "sm",
            variant: "primary",
            isPending: submitting,
            isDisabled: submitting || !valid,
            onPress: handleSubmit,
            children: form.scheduleEnabled ? "Schedule" : "Send"
          }
        )
      ] })
    ] });
  },
  {
    name: "NotificationSubmitController",
    monitors: (services) => [services.notifications.submit]
  }
);
NotificationSubmitController.Drawer = withDrawerController(NotificationSubmitController);
const NotificationsListPanel = ({ accountId }) => {
  const runtime = useRuntimeProvider();
  const canSend = runtime.user.hasPolicy(runtime.config.api.apiKey, "Notifications", "Send");
  const [selected, setSelected] = useState(null);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [isSubmitOpen, setSubmitOpen] = useState(false);
  const handleSelect = (notification) => {
    setSelected(notification);
    setHistoryOpen(true);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      NotificationsListController,
      {
        accountId,
        onSelect: handleSelect,
        onCreate: canSend ? () => setSubmitOpen(true) : void 0
      }
    ),
    selected && /* @__PURE__ */ jsx(
      NotificationHistoryController.Drawer,
      {
        isOpen: isHistoryOpen,
        onClose: () => setHistoryOpen(false),
        size: "md",
        placement: "right",
        notificationId: selected.id,
        notification: selected,
        classNames: { body: "px-5 py-4" },
        headerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-semibold", children: "Message History" }),
          /* @__PURE__ */ jsxs("span", { className: "text-muted text-xs", children: [
            "#",
            selected.id,
            selected.subject ? ` · ${selected.subject}` : ""
          ] })
        ] })
      }
    ),
    canSend && /* @__PURE__ */ jsx(
      NotificationSubmitController.Drawer,
      {
        isOpen: isSubmitOpen,
        onClose: () => setSubmitOpen(false),
        size: "lg",
        placement: "right",
        accountId,
        classNames: { body: "px-5 py-4" },
        headerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-semibold", children: "Send Message" }),
          /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Compose and submit a single message" })
        ] })
      }
    )
  ] });
};
const NotificationsListView = () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
  /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Messaging" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "History" })
  ] }) }),
  /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx(NotificationsListPanel, {}) }) })
] });
const NotificationsRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(NotificationsListView, {}) }) });
const channelOptions = () => Promise.resolve(Object.entries(NotificationChannel).map(([label, value]) => ({ value, label })));
const filters$1 = new Filters([
  new FilterSelect({ name: "channel", title: "Channel", loader: channelOptions }),
  new FilterInput({ name: "address", title: "Address" }),
  new FilterInput({ name: "reason", title: "Reason" }),
  new FilterInput({ name: "source", title: "Source" }),
  new FilterNumericInput({ name: "accountId", title: "Account ID", numberType: "integer" }),
  new FilterDateRange({ name: "createdOnUtc", title: "Opted Out" })
]);
const sorting$1 = new Sorting(
  [
    { value: "channel", title: "Channel" },
    { value: "address", title: "Address" },
    { value: "reason", title: "Reason" },
    { value: "source", title: "Source" },
    { value: "accountId", title: "Account ID" },
    { value: "createdOnUtc", title: "Opted Out" }
  ],
  "createdOnUtc",
  "desc"
);
const channelLabel = (channel) => Object.keys(NotificationChannel).find((name) => NotificationChannel[name] === channel) ?? channel;
const columnDefs$1 = [
  { propertyName: "channel", name: "Channel", isSortable: true, isResizable: true, minWidth: 120 },
  { propertyName: "address", name: "Address", isSortable: true, isResizable: true, minWidth: 200 },
  { propertyName: "reason", name: "Reason", isSortable: true, isResizable: true, minWidth: 180 },
  { propertyName: "source", name: "Source", isSortable: true, isResizable: true, minWidth: 150 },
  {
    propertyName: "accountId",
    name: "Account",
    isSortable: true,
    isResizable: true,
    minWidth: 130
  },
  {
    propertyName: "createdOnUtc",
    name: "Opted Out",
    isSortable: true,
    isResizable: true,
    minWidth: 150
  }
];
const OptOutsTable = ({ optOuts = [], onRemove }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "channel":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: channelLabel(cellValue) });
      case "createdOnUtc":
        return cellValue ? parseAndFormatDateTime(cellValue) : "";
      default:
        return cellValue ?? "";
    }
  }, []);
  const columns2 = useMemo(
    () => [
      ...columnDefs$1.map((column) => ({
        id: column.propertyName,
        header: column.name,
        isRowHeader: column.propertyName === "address",
        allowsSorting: column.isSortable,
        allowsResizing: column.isResizable,
        minWidth: column.minWidth,
        cell: (row) => renderCell(row, column.propertyName)
      })),
      // Pinned actions column: remove the opt-out.
      {
        id: "actions",
        header: "",
        pinned: "end",
        width: 56,
        minWidth: 56,
        align: "end",
        cell: (row) => /* @__PURE__ */ jsx(
          DeleteButton,
          {
            size: "sm",
            isIconOnly: true,
            label: "Remove",
            confirmColor: "danger",
            onPress: () => onRemove && onRemove(row)
          }
        )
      }
    ],
    [renderCell, onRemove]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Opt-outs list table",
      hideChips: true,
      columns: columns2,
      data: optOuts,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      topContent: /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-4", children: [
        /* @__PURE__ */ jsx(HorizontalFilters, { scope: "custom", classNames: { base: "grow-1" } }),
        /* @__PURE__ */ jsx(HorizontalChips, { scope: "global" })
      ] })
    }
  );
};
const OptOutsListController = reactive.component(
  ({ services, data, monitors, db: db2 }) => {
    const { optOuts } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    const handleRemove = useCallback(
      (optOut) => services.optOuts.optIn(optOut.channel, optOut.address),
      [services]
    );
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.optOuts.queryMql.action,
        action: services.optOuts.queryMql,
        filters: filters$1,
        sorting: sorting$1,
        paging: new Paging(50),
        isExecuting: monitors.queryMql.executing,
        skipInitial: () => db2.optOuts.toCollection().count(),
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(OptOutsTable, { optOuts, onRemove: handleRemove })
      }
    );
  },
  {
    name: "OptOutsListController",
    queries: (_, services) => [
      {
        name: "optOuts",
        execute: (db2) => db2.withRequestBroker(services.optOuts.queryMql.action, () => db2.optOuts, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.optOuts.queryMql]
  }
);
const OptOutsListView = () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
  /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Messaging" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Opt-Outs" })
  ] }) }),
  /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx(OptOutsListController, {}) }) })
] });
const OptOutsRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(OptOutsListView, {}) }) });
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    notifications: baseUrl + "/notifications",
    batches: baseUrl + "/batches",
    settings: baseUrl + "/settings"
  };
  return {
    notifications: {
      list: () => rootPaths.notifications,
      create: () => `${rootPaths.notifications}/create`,
      details: (id) => `${rootPaths.notifications}/${id}/details`
    },
    batches: {
      list: () => rootPaths.batches,
      create: () => `${rootPaths.batches}/create`,
      details: (id) => `${rootPaths.batches}/${id}/details`
    },
    settings: {
      list: () => rootPaths.settings
    }
  };
};
const statusOptions = () => Promise.resolve(
  Object.keys(NotificationBatchStatusType).map((name) => ({ value: name, label: name }))
);
const filters = new Filters([
  new FilterInput({ name: "name", title: "Name" }),
  new FilterInput({ name: "source", title: "Source" }),
  new FilterSelect({ name: "statusName", title: "Status", loader: statusOptions }),
  new FilterDateRange({ name: "createdOnUtc", title: "Created On" })
]);
const sorting = new Sorting(
  [
    { value: "name", title: "Name" },
    { value: "source", title: "Source" },
    { value: "statusName", title: "Status" },
    { value: "totalCount", title: "Total" },
    { value: "createdOnUtc", title: "Created On" }
  ],
  "createdOnUtc",
  "desc"
);
const statusColor$2 = (status) => {
  switch (status) {
    case "Completed":
      return "success";
    case "Processing":
      return "accent";
    default:
      return "default";
  }
};
const columnDefs = [
  { propertyName: "name", name: "Name", isSortable: true, isResizable: true, minWidth: 180 },
  { propertyName: "source", name: "Source", isSortable: true, isResizable: true, minWidth: 150 },
  {
    propertyName: "statusName",
    name: "Status",
    isSortable: true,
    isResizable: true,
    minWidth: 130
  },
  { propertyName: "totalCount", name: "Total", isSortable: true, isResizable: true, minWidth: 90 },
  { propertyName: "sentCount", name: "Sent", isSortable: false, isResizable: true, minWidth: 90 },
  {
    propertyName: "deliveredCount",
    name: "Delivered",
    isSortable: false,
    isResizable: true,
    minWidth: 110
  },
  {
    propertyName: "failedCount",
    name: "Failed",
    isSortable: false,
    isResizable: true,
    minWidth: 90
  },
  {
    propertyName: "createdOnUtc",
    name: "Created On",
    isSortable: true,
    isResizable: true,
    minWidth: 170
  }
];
const BatchesTable = ({ batches = [], onSelect, onCreate }) => {
  const runtime = useRuntimeProvider();
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "statusName":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: statusColor$2(cellValue), size: "sm", children: cellValue });
      case "createdOnUtc":
        return cellValue ? parseAndFormatDateTime(cellValue) : "";
      default:
        return cellValue ?? "";
    }
  }, []);
  const columns2 = useMemo(
    () => columnDefs.map((column) => ({
      id: column.propertyName,
      header: column.name,
      isRowHeader: column.propertyName === "name",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Batches list table",
      hideChips: true,
      columns: columns2,
      data: batches,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      createLabel: "New Batch",
      onCreate,
      createEnabled: runtime.user.hasPolicy(runtime.config.api.apiKey, "bulknotifications", "send"),
      onRowAction: (key) => {
        const selected = batches.find((item) => item.id === key);
        if (selected && onSelect) onSelect(selected);
      },
      topContent: /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-4", children: [
        /* @__PURE__ */ jsx(HorizontalFilters, { scope: "custom", classNames: { base: "grow-1" } }),
        /* @__PURE__ */ jsx(HorizontalChips, { scope: "global" })
      ] })
    }
  );
};
const BatchesListController = reactive.component(
  ({ services, data, monitors, db: db2, onSelect, onCreate }) => {
    const { batches } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.batches.queryMql.action,
        action: services.batches.queryMql,
        filters,
        sorting,
        paging: new Paging(50),
        isExecuting: monitors.queryMql.executing,
        skipInitial: () => db2.batchesPage.toCollection().count(),
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(BatchesTable, { batches, onSelect: (item) => onSelect && onSelect(item), onCreate })
      }
    );
  },
  {
    name: "BatchesListController",
    queries: (_, services) => [
      {
        name: "batches",
        execute: (db2) => db2.withRequestBroker(services.batches.queryMql.action, () => db2.batchesPage, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.batches.queryMql]
  }
);
const BatchesListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
    /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Messaging" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Batches" })
    ] }) }),
    /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx(
      BatchesListController,
      {
        onSelect: (batch) => navigate(RoutePaths().batches.details(batch.id)),
        onCreate: () => navigate(RoutePaths().batches.create())
      }
    ) }) })
  ] });
};
const statusColor$1 = (status) => {
  switch (status) {
    case "Sent":
    case "Delivered":
      return "success";
    case "Failed":
    case "Bounced":
      return "danger";
    case "Suppressed":
    case "Canceled":
      return "default";
    default:
      return "accent";
  }
};
const columns$2 = [
  { propertyName: "channelName", name: "Channel" },
  { propertyName: "statusName", name: "Status" },
  { propertyName: "toAddress", name: "To" },
  { propertyName: "subject", name: "Subject" },
  { propertyName: "sentOnUtc", name: "Sent On" }
];
const BatchNotificationsTable = ({ notifications = [], onSelect }) => {
  const renderCell = useCallback((row, key) => {
    const value = row[key];
    switch (key) {
      case "channelName":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: value });
      case "statusName":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: statusColor$1(value), size: "sm", children: value });
      case "sentOnUtc":
        return value ? parseAndFormatDateTime(value) : "";
      default:
        return value ?? "";
    }
  }, []);
  return /* @__PURE__ */ jsx(Table, { className: "h-full", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { className: "h-full", children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Batch notifications", children: [
    /* @__PURE__ */ jsx(Table.Header, { className: "sticky top-0 z-10", children: columns$2.map((column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, children: column.name }, column.propertyName)) }),
    /* @__PURE__ */ jsx(Table.Body, { items: notifications, children: (row) => /* @__PURE__ */ jsx(
      Table.Row,
      {
        id: row.id,
        onAction: () => onSelect && onSelect(row),
        className: "cursor-pointer",
        children: columns$2.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName))
      },
      row.id
    ) })
  ] }) }) });
};
const statusColor = (status) => {
  switch (status) {
    case "Completed":
      return "success";
    case "Processing":
      return "accent";
    default:
      return "default";
  }
};
const COUNT_FIELDS = [
  ["Total", "totalCount"],
  ["Sent", "sentCount"],
  ["Delivered", "deliveredCount"],
  ["Failed", "failedCount"],
  ["Suppressed", "suppressedCount"],
  ["Canceled", "canceledCount"]
];
const HAS_PLACEHOLDER = /\{\{\s*[^{}]+?\s*\}\}/;
const sampleText = (notification) => `${notification.subject ?? ""}${notification.body ?? ""}`;
const parseTemplateData = (notification) => {
  try {
    return JSON.parse(notification.templateData ?? "{}");
  } catch {
    return {};
  }
};
const messagePerChannel = (notifications) => {
  const byChannel = /* @__PURE__ */ new Map();
  for (const notification of notifications) {
    const key = notification.channelName ?? notification.channel;
    if (!byChannel.has(key)) byChannel.set(key, []);
    byChannel.get(key).push(notification);
  }
  return [...byChannel.values()].map((group) => {
    const resolved = group.filter((notification) => !HAS_PLACEHOLDER.test(sampleText(notification)));
    const pool = resolved.length > 0 ? resolved : group;
    const sample = pool[Math.floor(Math.random() * pool.length)];
    if (!HAS_PLACEHOLDER.test(sampleText(sample))) return sample;
    const values = parseTemplateData(sample);
    return {
      ...sample,
      subject: substitutePlaceholders(sample.subject, values),
      body: substitutePlaceholders(sample.body, values)
    };
  });
};
const BatchDetailController = reactive.component(
  ({ services, data, monitors, batchId }) => {
    const { batch, batchNotifications } = data;
    const [selected, setSelected] = useState(null);
    const [isHistoryOpen, setHistoryOpen] = useState(false);
    const refreshing = monitors.queryByBatch.executing;
    const messages = useMemo(() => messagePerChannel(batchNotifications), [batchNotifications]);
    const refresh = () => {
      services.batches.getById(batchId);
      services.notifications.queryByBatch(batchId);
    };
    if (!batch?.id) {
      return /* @__PURE__ */ jsx("div", { className: "flex justify-center py-8", children: /* @__PURE__ */ jsx(Spinner, {}) });
    }
    return /* @__PURE__ */ jsxs("div", { className: "flex h-full flex-col gap-4", children: [
      /* @__PURE__ */ jsx("div", { className: "bg-surface flex flex-col gap-3 rounded-2xl p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
            /* @__PURE__ */ jsx("h2", { className: "text-foreground text-lg font-semibold", children: batch.name || "Untitled batch" }),
            /* @__PURE__ */ jsx(Chip, { variant: "soft", color: statusColor(batch.statusName), size: "sm", children: batch.statusName })
          ] }),
          /* @__PURE__ */ jsxs("p", { className: "text-muted text-xs", children: [
            batch.source,
            batch.sourceKey ? ` · ${batch.sourceKey}` : "",
            " ·",
            " ",
            parseAndFormatDateTime(batch.createdOnUtc)
          ] })
        ] }),
        /* @__PURE__ */ jsx(ReloadButton, { isLoading: refreshing, onPress: refresh, variant: "secondary" })
      ] }) }),
      /* @__PURE__ */ jsx(KPIGroup, { children: COUNT_FIELDS.map(([label, field2], index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
        index > 0 && /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
        /* @__PURE__ */ jsxs(KPI, { children: [
          /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: label }) }),
          /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsx(KPI.Value, { value: batch[field2] ?? 0, maximumFractionDigits: 0 }) })
        ] })
      ] }, field2)) }),
      messages.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm font-medium", children: "Message" }),
        /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: messages.map((notification) => /* @__PURE__ */ jsx(
          NotificationContent,
          {
            notification,
            showRecipient: false
          },
          notification.id
        )) })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1", children: /* @__PURE__ */ jsx(
        BatchNotificationsTable,
        {
          notifications: batchNotifications,
          onSelect: (notification) => {
            setSelected(notification);
            setHistoryOpen(true);
          }
        }
      ) }),
      selected && /* @__PURE__ */ jsx(
        NotificationHistoryController.Drawer,
        {
          isOpen: isHistoryOpen,
          onClose: () => setHistoryOpen(false),
          size: "md",
          placement: "right",
          notificationId: selected.id,
          notification: selected,
          classNames: { body: "px-5 py-4" },
          headerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-semibold", children: "Message History" }),
            /* @__PURE__ */ jsxs("span", { className: "text-muted text-xs", children: [
              "#",
              selected.id,
              selected.subject ? ` · ${selected.subject}` : ""
            ] })
          ] })
        }
      )
    ] });
  },
  {
    name: "BatchDetailController",
    sync: (services, { batchId }) => [
      {
        execute: () => Number.isFinite(batchId) ? services.batches.getById(batchId) : Promise.resolve(),
        deps: [batchId]
      },
      {
        execute: () => Number.isFinite(batchId) ? services.notifications.queryByBatch(batchId) : Promise.resolve(),
        deps: [batchId]
      }
    ],
    queries: ({ batchId }) => [
      {
        name: "batch",
        defaultValue: null,
        execute: (db2) => Number.isFinite(batchId) ? db2.batches.get(batchId) : null,
        deps: [batchId]
      },
      {
        name: "batchNotifications",
        defaultValue: [],
        execute: (db2) => db2.batchNotifications.orderBy("createdOnUtc").reverse().toArray(),
        deps: [batchId]
      }
    ],
    monitors: (services) => [services.notifications.queryByBatch]
  }
);
const BatchDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const batchId = Number(id);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
    /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Notifications" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().batches.list()), children: "Batches" }),
      /* @__PURE__ */ jsxs(BreadcrumbsItem, { children: [
        "#",
        batchId
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx(BatchDetailController, { batchId }) }) })
  ] });
};
const SPECIAL_FIELDS = [
  { name: "mobilePhone", label: "Mobile Phone", optional: true },
  { name: "homePhone", label: "Home Phone", optional: true },
  { name: "email", label: "Email", optional: true },
  { name: "accountId", label: "accountId", optional: true }
];
const RESERVED_STEMS = new Set(
  SPECIAL_FIELDS.flatMap((field2) => [field2.name, field2.label]).map(
    (value) => value.trim().toLowerCase()
  )
);
const ImportStep = ({ onDone }) => {
  const rowsRef = useRef([]);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsxs(Alert, { status: "accent", className: "bg-accent-soft mx-auto w-full max-w-2xl", children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsxs(Alert.Content, { children: [
        /* @__PURE__ */ jsx(Alert.Title, { children: "Column mapping" }),
        /* @__PURE__ */ jsxs(Alert.Description, { children: [
          "Map the contact columns — ",
          /* @__PURE__ */ jsx("strong", { children: "Mobile Phone" }),
          " (SMS),",
          " ",
          /* @__PURE__ */ jsx("strong", { children: "Home Phone" }),
          " (Voice), and/or ",
          /* @__PURE__ */ jsx("strong", { children: "Email" }),
          " — plus optional",
          " ",
          /* @__PURE__ */ jsx("strong", { children: "Account ID" }),
          ". You'll pick which channels to send next; each row is sent on a channel only when it has that contact. Columns whose header matches a field are mapped automatically; every other column is imported too and can be used as a",
          " ",
          /* @__PURE__ */ jsx("code", { children: "{{placeholder}}" }),
          " in the message content."
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      CsvImporter,
      {
        onStart: () => {
          rowsRef.current = [];
        },
        dataHandler: (rows) => {
          rowsRef.current.push(...rows.map((row) => row.data));
        },
        onComplete: () => {
          const rows = rowsRef.current;
          onDone(rows, rows.length ? Object.keys(rows[0]) : []);
        },
        children: ({ preview }) => /* @__PURE__ */ jsxs(Fragment, { children: [
          SPECIAL_FIELDS.map((field2) => /* @__PURE__ */ jsx(
            ImporterField,
            {
              name: field2.name,
              label: field2.label,
              ...field2.optional ? { optional: true } : {}
            },
            field2.name
          )),
          preview?.columns?.map(({ header, index }) => {
            const name = header?.trim();
            return name && !RESERVED_STEMS.has(name.toLowerCase()) ? /* @__PURE__ */ jsx(ImporterField, { name, label: name, optional: true }, index) : null;
          })
        ] })
      }
    )
  ] });
};
const CHANNEL_OPTIONS = [
  { key: "sms", label: "SMS", hint: "to Mobile Phone" },
  { key: "voice", label: "Voice", hint: "to Home Phone" },
  { key: "dialer", label: "Dialer", hint: "dials Mobile Phone" },
  { key: "email", label: "Email", hint: "to Email" }
];
const PREVIEW = {
  sms: (content, row) => ({
    channelName: "SMS",
    body: substitutePlaceholders(content.smsBody, row)
  }),
  voice: (content, row) => ({
    channelName: "Voice",
    body: substitutePlaceholders(content.voiceBody, row)
  }),
  email: (content, row) => ({
    channelName: "Email",
    subject: substitutePlaceholders(content.emailSubject, row),
    body: substitutePlaceholders(content.emailBodyHtml, row),
    isHtml: true
  })
};
const ContentStep = ({
  channels,
  onChannelsChange,
  columns: columns2,
  sampleRow,
  content,
  onChange
}) => {
  const [active, setActive] = useState(null);
  const toggle = (key) => onChannelsChange((prev) => ({ ...prev, [key]: !prev[key] }));
  const selected = CHANNEL_OPTIONS.filter((option) => channels[option.key]);
  const activeKey = selected.some((option) => option.key === active) ? active : selected[0]?.key;
  const preview = PREVIEW[activeKey]?.(content, sampleRow);
  const hasPreview = Boolean(preview?.body?.trim() || preview?.subject?.trim());
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm font-medium", children: "Channels" }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-4", children: CHANNEL_OPTIONS.map(({ key, label, hint }) => /* @__PURE__ */ jsxs("label", { className: "text-foreground flex items-center gap-2 text-sm", children: [
        /* @__PURE__ */ jsx("input", { type: "checkbox", checked: channels[key], onChange: () => toggle(key) }),
        label,
        " ",
        /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: hint })
      ] }, key)) })
    ] }),
    selected.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "Select one or more channels above to compose content." }) : /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsx(Segment, { selectedKey: activeKey, onSelectionChange: setActive, children: selected.map((option) => /* @__PURE__ */ jsx(Segment.Item, { id: option.key, children: option.label }, option.key)) }),
      channels.sms && /* @__PURE__ */ jsx("div", { className: activeKey === "sms" ? "block" : "hidden", children: /* @__PURE__ */ jsx(
        SmsContentInput,
        {
          value: content.smsBody,
          onChange: (smsBody) => onChange((prev) => ({ ...prev, smsBody })),
          columns: columns2,
          sampleRow
        }
      ) }),
      channels.voice && /* @__PURE__ */ jsxs("div", { className: activeKey === "voice" ? "flex flex-col gap-3" : "hidden", children: [
        /* @__PURE__ */ jsx(
          SmsContentInput,
          {
            value: content.voiceBody,
            onChange: (voiceBody) => onChange((prev) => ({ ...prev, voiceBody })),
            columns: columns2,
            showSegments: false,
            noun: "Voice"
          }
        ),
        /* @__PURE__ */ jsx(
          VoiceRepeatField,
          {
            value: content.voiceRepeat,
            onChange: (voiceRepeat) => onChange((prev) => ({ ...prev, voiceRepeat }))
          }
        )
      ] }),
      channels.dialer && /* @__PURE__ */ jsx("div", { className: activeKey === "dialer" ? "block" : "hidden", children: /* @__PURE__ */ jsx(
        DialerCampaignPicker,
        {
          value: content.dialer,
          onChange: (dialer) => onChange((prev) => ({ ...prev, dialer }))
        }
      ) }),
      channels.email && /* @__PURE__ */ jsx("div", { className: activeKey === "email" ? "block" : "hidden", children: /* @__PURE__ */ jsx(
        EmailContentInput,
        {
          subject: content.emailSubject,
          onSubjectChange: (emailSubject) => onChange((prev) => ({ ...prev, emailSubject })),
          onBodyChange: (emailBodyHtml) => onChange((prev) => ({ ...prev, emailBodyHtml })),
          columns: columns2
        }
      ) }),
      hasPreview && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm font-medium", children: "Preview" }),
        /* @__PURE__ */ jsx(NotificationContent, { notification: preview, showRecipient: false }),
        /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Rendered with the values of one imported row; every recipient gets their own." })
      ] })
    ] })
  ] });
};
const INPUT_CLASS$2 = "bg-surface-secondary text-foreground placeholder:text-muted w-full rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const percent = (part, total) => total > 0 ? Math.round(part / total * 100) : 0;
const has = (row, key) => String(row?.[key] ?? "").trim().length > 0;
const ReviewStep = ({ rows, channels, name, onNameChange }) => {
  const smsCount = channels.sms ? rows.filter((row) => has(row, "mobilePhone")).length : 0;
  const voiceCount = channels.voice ? rows.filter((row) => has(row, "homePhone")).length : 0;
  const dialerCount = channels.dialer ? rows.filter((row) => has(row, "mobilePhone")).length : 0;
  const emailCount = channels.email ? rows.filter((row) => has(row, "email")).length : 0;
  const notificationCount = smsCount + voiceCount + dialerCount + emailCount;
  const skipped = rows.filter(
    (row) => !(channels.sms && has(row, "mobilePhone") || channels.voice && has(row, "homePhone") || channels.dialer && has(row, "mobilePhone") || channels.email && has(row, "email"))
  ).length;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
        "Batch name ",
        /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
      ] }),
      /* @__PURE__ */ jsx(
        "input",
        {
          value: name,
          onChange: (event) => onNameChange(event.target.value),
          placeholder: "Name for this batch (required)",
          "aria-required": "true",
          className: INPUT_CLASS$2
        }
      ),
      !name.trim() && /* @__PURE__ */ jsx("span", { className: "text-danger text-xs", children: "A batch name is required." })
    ] }),
    /* @__PURE__ */ jsxs(KPIGroup, { children: [
      /* @__PURE__ */ jsxs(KPI, { children: [
        /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: "Notifications" }) }),
        /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsx(KPI.Value, { value: notificationCount, maximumFractionDigits: 0 }) })
      ] }),
      channels.sms && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
        /* @__PURE__ */ jsxs(KPI, { children: [
          /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: "SMS" }) }),
          /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-2", children: [
            /* @__PURE__ */ jsx(KPI.Value, { value: smsCount, maximumFractionDigits: 0 }),
            /* @__PURE__ */ jsxs("span", { className: "text-muted text-sm", children: [
              percent(smsCount, notificationCount),
              "% of total"
            ] })
          ] }) })
        ] })
      ] }),
      channels.voice && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
        /* @__PURE__ */ jsxs(KPI, { children: [
          /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: "Voice" }) }),
          /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-2", children: [
            /* @__PURE__ */ jsx(KPI.Value, { value: voiceCount, maximumFractionDigits: 0 }),
            /* @__PURE__ */ jsxs("span", { className: "text-muted text-sm", children: [
              percent(voiceCount, notificationCount),
              "% of total"
            ] })
          ] }) })
        ] })
      ] }),
      channels.dialer && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
        /* @__PURE__ */ jsxs(KPI, { children: [
          /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: "Dialer" }) }),
          /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-2", children: [
            /* @__PURE__ */ jsx(KPI.Value, { value: dialerCount, maximumFractionDigits: 0 }),
            /* @__PURE__ */ jsxs("span", { className: "text-muted text-sm", children: [
              percent(dialerCount, notificationCount),
              "% of total"
            ] })
          ] }) })
        ] })
      ] }),
      channels.email && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
        /* @__PURE__ */ jsxs(KPI, { children: [
          /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: "Email" }) }),
          /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-2", children: [
            /* @__PURE__ */ jsx(KPI.Value, { value: emailCount, maximumFractionDigits: 0 }),
            /* @__PURE__ */ jsxs("span", { className: "text-muted text-sm", children: [
              percent(emailCount, notificationCount),
              "% of total"
            ] })
          ] }) })
        ] })
      ] }),
      skipped > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
        /* @__PURE__ */ jsxs(KPI, { children: [
          /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: "Skipped rows" }) }),
          /* @__PURE__ */ jsx(KPI.Content, { children: /* @__PURE__ */ jsx(KPI.Value, { value: skipped, maximumFractionDigits: 0 }) })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "text-muted text-xs", children: [
      "Each row sends one notification per selected channel it has a contact for;",
      " ",
      /* @__PURE__ */ jsx("code", { children: "{{placeholder}}" }),
      " tokens are replaced with that row's values before sending."
    ] })
  ] });
};
const STEPS = [
  { title: "Import", description: "Upload & map" },
  { title: "Content", description: "Compose messages" },
  { title: "Review", description: "Confirm & submit" }
];
const EMPTY_CHANNELS = { sms: false, voice: false, dialer: false, email: false };
const toAccountId = (value) => {
  if (value == null || value === "") return void 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : void 0;
};
const field = (row, key) => {
  const value = String(row?.[key] ?? "").trim();
  return value || void 0;
};
const buildNotifications = (row, channels, content) => {
  const accountId = toAccountId(row.accountId);
  const notifications = [];
  const mobilePhone = field(row, "mobilePhone");
  if (channels.sms && mobilePhone) {
    notifications.push({
      channel: NotificationChannel.Sms,
      toAddress: mobilePhone,
      body: substitutePlaceholders(content.smsBody, row),
      isHtml: false,
      accountId
    });
  }
  if (channels.dialer && mobilePhone && content.dialer?.campaignKey) {
    notifications.push({
      channel: NotificationChannel.Dialer,
      toAddress: mobilePhone,
      campaignKey: content.dialer.campaignKey,
      isHtml: false,
      accountId
    });
  }
  const homePhone = field(row, "homePhone");
  if (channels.voice && homePhone) {
    notifications.push({
      channel: NotificationChannel.Voice,
      toAddress: homePhone,
      body: substitutePlaceholders(content.voiceBody, row),
      isHtml: false,
      voiceRepeatCount: resolveVoiceRepeatCount(content.voiceRepeat),
      accountId
    });
  }
  const email = field(row, "email");
  if (channels.email && email) {
    notifications.push({
      channel: NotificationChannel.Email,
      toAddress: email,
      subject: substitutePlaceholders(content.emailSubject, row),
      body: substitutePlaceholders(content.emailBodyHtml, row),
      isHtml: true,
      accountId
    });
  }
  return notifications;
};
const isContentComplete = (channels, content) => (!channels.sms || content.smsBody.trim().length > 0) && (!channels.voice || content.voiceBody.trim().length > 0) && (!channels.dialer || Boolean(content.dialer?.campaignKey)) && (!channels.email || content.emailSubject.trim().length > 0 && content.emailBodyHtml.trim().length > 0);
const BatchCreateView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [rows, setRows] = useState([]);
    const [columns2, setColumns] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState(EMPTY_CHANNELS);
    const [content, setContent] = useState({
      smsBody: "",
      voiceBody: "",
      voiceRepeat: { enabled: false, count: 3 },
      dialer: { dialerId: null, campaignId: null, campaignKey: null },
      emailSubject: "",
      emailBodyHtml: ""
    });
    const [name, setName] = useState("");
    useSuccessReactor(
      services.batches.bulkSubmit,
      (batch) => navigate(RoutePaths().batches.details(batch.id))
    );
    const submitting = monitors.bulkSubmit.executing;
    const anyChannel = selectedChannels.sms || selectedChannels.voice || selectedChannels.email;
    const sampleRow = useMemo(() => {
      const complete = rows.filter(
        (row) => Object.values(row).every((value) => String(value ?? "").trim().length > 0)
      );
      const pool = complete.length > 0 ? complete : rows;
      return pool[Math.floor(Math.random() * pool.length)];
    }, [rows]);
    const notifications = useMemo(
      () => rows.flatMap((row) => buildNotifications(row, selectedChannels, content)),
      [rows, selectedChannels, content]
    );
    const canSubmit = notifications.length > 0 && name.trim().length > 0 && isContentComplete(selectedChannels, content);
    const handleSubmit = () => {
      if (!canSubmit) return;
      services.batches.bulkSubmit({
        source: "notifications-ui",
        name: name.trim(),
        notifications
      });
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
      /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Notifications" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().batches.list()), children: "Bulk Notifications" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "New" })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex h-full flex-col gap-4 p-4", children: [
        /* @__PURE__ */ jsx(Stepper, { currentStep: step, children: STEPS.map((item) => /* @__PURE__ */ jsxs(Stepper.Step, { children: [
          /* @__PURE__ */ jsx(Stepper.Indicator, {}),
          /* @__PURE__ */ jsxs(Stepper.Content, { children: [
            /* @__PURE__ */ jsx(Stepper.Title, { children: item.title }),
            /* @__PURE__ */ jsx(Stepper.Description, { children: item.description })
          ] }),
          /* @__PURE__ */ jsx(Stepper.Separator, {})
        ] }, item.title)) }),
        /* @__PURE__ */ jsxs("div", { className: "min-h-0 flex-1 overflow-auto", children: [
          step === 0 && /* @__PURE__ */ jsx(
            ImportStep,
            {
              onDone: (importedRows, importedColumns) => {
                setRows(importedRows);
                setColumns(importedColumns);
                setStep(1);
              }
            }
          ),
          rows.length > 0 && /* @__PURE__ */ jsx("div", { className: step === 1 ? "block" : "hidden", children: /* @__PURE__ */ jsx(
            ContentStep,
            {
              channels: selectedChannels,
              onChannelsChange: setSelectedChannels,
              columns: columns2,
              sampleRow,
              content,
              onChange: setContent
            }
          ) }),
          step === 2 && /* @__PURE__ */ jsx(ReviewStep, { rows, channels: selectedChannels, name, onNameChange: setName })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: "tertiary",
              isDisabled: step === 0,
              onPress: () => setStep((current) => Math.max(0, current - 1)),
              children: "Back"
            }
          ),
          step === 1 && /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: "primary",
              isDisabled: !anyChannel,
              onPress: () => setStep(2),
              children: "Next"
            }
          ),
          step === 2 && /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: "primary",
              isPending: submitting,
              isDisabled: submitting || !canSubmit,
              onPress: handleSubmit,
              children: "Submit batch"
            }
          )
        ] })
      ] }) })
    ] });
  },
  { name: "BatchCreateView", monitors: (services) => [services.batches.bulkSubmit] }
);
const BatchesRoutes = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/create", element: /* @__PURE__ */ jsx(BatchCreateView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: "/:id/details", element: /* @__PURE__ */ jsx(BatchDetailView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(BatchesListView, {}) })
] });
const columns$1 = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  { key: "dialerCampaignKey", label: "Dialer Campaign Key" },
  { key: "uniqueKey", label: "Unique Key" },
  { key: "createdOnUtc", label: "Created" },
  { key: "actions", label: "" }
];
const CampaignsTable = ({ campaigns = [], onEdit, onRemove }) => {
  const renderCell = useCallback(
    (row, key) => {
      switch (key) {
        case "uniqueKey":
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "text-muted font-mono text-xs", children: `${String(row.uniqueKey).slice(0, 12)}…` }),
            /* @__PURE__ */ jsx(CopyToClipboard, { text: row.uniqueKey })
          ] });
        case "createdOnUtc":
          return row.createdOnUtc ? parseAndFormatDateTime(row.createdOnUtc) : "";
        case "actions":
          return /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-1", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                size: "sm",
                isIconOnly: true,
                variant: "tertiary",
                "aria-label": "Edit",
                onPress: () => onEdit?.(row),
                children: /* @__PURE__ */ jsx("i", { className: "ri-pencil-line" })
              }
            ),
            /* @__PURE__ */ jsx(
              DeleteButton,
              {
                size: "sm",
                isIconOnly: true,
                label: "Delete",
                confirmColor: "danger",
                onPress: () => onRemove?.(row)
              }
            )
          ] });
        default:
          return row[key] ?? "";
      }
    },
    [onEdit, onRemove]
  );
  return /* @__PURE__ */ jsx(Table, { className: "h-full", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { className: "h-full", children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Campaigns", children: [
    /* @__PURE__ */ jsx(Table.Header, { children: columns$1.map((column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.key, children: column.label }, column.key)) }),
    /* @__PURE__ */ jsx(Table.Body, { items: campaigns, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, children: columns$1.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.key) }, column.key)) }, row.id) })
  ] }) }) });
};
const INPUT_CLASS$1 = "bg-surface-secondary text-foreground placeholder:text-muted w-full rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const formFrom$1 = (campaign) => campaign ? {
  name: campaign.name ?? "",
  description: campaign.description ?? "",
  dialerCampaignKey: campaign.dialerCampaignKey ?? ""
} : { name: "", description: "", dialerCampaignKey: "" };
const CampaignForm = reactive.component(
  ({ services, monitors, dialerId, campaign, onClose }) => {
    const [form, setForm] = useState(() => formFrom$1(campaign));
    const saving = monitors.createCampaign.executing || monitors.updateCampaign.executing;
    const valid = form.name.trim().length > 0 && form.dialerCampaignKey.trim().length > 0;
    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    useSuccessReactor(services.dialers.createCampaign, () => onClose?.());
    useSuccessReactor(services.dialers.updateCampaign, () => onClose?.());
    const save = () => {
      if (!valid) return;
      const request = {
        name: form.name.trim(),
        description: form.description.trim() || void 0,
        dialerCampaignKey: form.dialerCampaignKey.trim()
      };
      if (campaign) {
        services.dialers.updateCampaign(dialerId, campaign.id, request);
      } else {
        services.dialers.createCampaign(dialerId, request);
      }
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      campaign && /* @__PURE__ */ jsx(TextFieldWithCopy, { label: "Unique Key", value: campaign.uniqueKey, isReadOnly: true }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
          "Name ",
          /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsx(
          "input",
          {
            value: form.name,
            onChange: (event) => update({ name: event.target.value }),
            placeholder: "Campaign name",
            className: INPUT_CLASS$1
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("label", { className: "text-foreground text-sm font-medium", children: "Description" }),
        /* @__PURE__ */ jsx(
          "textarea",
          {
            value: form.description,
            onChange: (event) => update({ description: event.target.value }),
            placeholder: "Optional",
            rows: 3,
            className: `${INPUT_CLASS$1} resize-y`
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
          "Dialer Campaign Key ",
          /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsx(
          "input",
          {
            value: form.dialerCampaignKey,
            onChange: (event) => update({ dialerCampaignKey: event.target.value }),
            placeholder: "The campaign's identifier in the dialer system",
            className: INPUT_CLASS$1
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2 pt-2", children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", onPress: () => onClose?.(), children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            size: "sm",
            variant: "primary",
            isPending: saving,
            isDisabled: saving || !valid,
            onPress: save,
            children: campaign ? "Save" : "Create"
          }
        )
      ] })
    ] });
  },
  {
    name: "CampaignForm",
    monitors: (services) => [services.dialers.createCampaign, services.dialers.updateCampaign]
  }
);
CampaignForm.Drawer = withDrawerController(CampaignForm);
const CampaignsController = reactive.component(
  ({ services, data, dialerId }) => {
    const { campaigns = [] } = data;
    const [editing, setEditing] = useState(null);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const openForm = (campaign) => {
      setEditing(campaign);
      setFormKey((key) => key + 1);
      setDrawerOpen(true);
    };
    const remove = (campaign) => services.dialers.deleteCampaign(dialerId, campaign.id);
    return /* @__PURE__ */ jsxs("div", { className: "flex min-h-0 flex-1 flex-col gap-3", children: [
      /* @__PURE__ */ jsx("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", onPress: () => openForm(null), children: "Add campaign" }) }),
      campaigns.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "No campaigns for this dialer yet." }) : /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1", children: /* @__PURE__ */ jsx(CampaignsTable, { campaigns, onEdit: openForm, onRemove: remove }) }),
      /* @__PURE__ */ jsx(
        CampaignForm.Drawer,
        {
          isOpen: isDrawerOpen,
          onClose: () => setDrawerOpen(false),
          size: "md",
          placement: "right",
          classNames: { body: "px-5 py-4" },
          dialerId,
          campaign: editing,
          headerContent: () => /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-semibold", children: editing ? "Edit campaign" : "New campaign" })
        },
        formKey
      )
    ] });
  },
  {
    name: "CampaignsController",
    sync: (services, { dialerId }) => [
      {
        execute: () => dialerId != null ? services.dialers.getCampaigns(dialerId) : Promise.resolve(),
        deps: [dialerId]
      }
    ],
    queries: ({ dialerId }) => [
      {
        name: "campaigns",
        defaultValue: [],
        execute: (db2) => dialerId != null ? db2.campaigns.where("dialerId").equals(dialerId).toArray() : [],
        deps: [dialerId]
      }
    ]
  }
);
const toKey = (value) => value != null ? String(value) : null;
const fromKey = (key) => key != null && key !== "" ? Number(key) : null;
const CampaignsManager = reactive.component(
  ({ data }) => {
    const { dialers = [] } = data;
    const [dialerId, setDialerId] = useState(null);
    const effectiveDialerId = dialerId ?? dialers[0]?.id ?? null;
    return /* @__PURE__ */ jsxs("div", { className: "flex min-h-0 flex-1 flex-col gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex max-w-xs flex-col gap-1", children: [
        /* @__PURE__ */ jsx("label", { className: "text-foreground text-sm font-medium", children: "Dialer" }),
        /* @__PURE__ */ jsxs(
          Select,
          {
            "aria-label": "Dialer",
            isDisabled: dialers.length === 0,
            value: toKey(effectiveDialerId),
            onChange: (key) => setDialerId(fromKey(key)),
            children: [
              /* @__PURE__ */ jsxs(Select.Trigger, { className: "bg-surface-secondary", children: [
                /* @__PURE__ */ jsx(Select.Value, { children: ({ state }) => {
                  const selected = dialers.find(
                    (item) => String(item.id) === String(state.selectedItems?.[0]?.key)
                  );
                  return selected ? selected.name : "Select a dialer";
                } }),
                /* @__PURE__ */ jsx(Select.Indicator, {})
              ] }),
              /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { children: dialers.map((dialer) => /* @__PURE__ */ jsxs(ListBox.Item, { id: String(dialer.id), textValue: dialer.name, children: [
                dialer.name,
                /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
              ] }, dialer.id)) }) })
            ]
          }
        )
      ] }),
      effectiveDialerId != null ? /* @__PURE__ */ jsx(CampaignsController, { dialerId: effectiveDialerId }) : /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "No dialers are configured." })
    ] });
  },
  {
    name: "CampaignsManager",
    sync: (services) => [{ execute: () => services.dialers.getDialers(), deps: [] }],
    queries: () => [{ name: "dialers", defaultValue: [], execute: (db2) => db2.dialers.toArray(), deps: [] }]
  }
);
const columns = [
  { key: "name", label: "Name" },
  { key: "kind", label: "Kind" },
  { key: "description", label: "Description" },
  { key: "hasWebhookUrl", label: "Webhook" },
  { key: "uniqueKey", label: "Unique Key" },
  { key: "createdOnUtc", label: "Created" },
  { key: "actions", label: "" }
];
const kindLabel = (row) => row.kindName || (row.kind === TeamsDestinationKind.DirectMessage ? "Direct Message" : "Channel");
const testTooltip = (row) => {
  if (!row.hasWebhookUrl) return "Add a webhook URL before sending a test message";
  return row.kind === TeamsDestinationKind.DirectMessage ? "Send a test message to yourself in Teams" : "Send a test message to this channel";
};
const TeamsDestinationsTable = ({
  destinations = [],
  canUpdate,
  canDelete,
  testingId,
  onEdit,
  onRemove,
  onTest
}) => {
  const renderCell = useCallback(
    (row, key) => {
      switch (key) {
        case "name":
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { children: row.name }),
            row.isDefault && /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: "Default" })
          ] });
        case "kind":
          return kindLabel(row);
        case "hasWebhookUrl":
          return row.hasWebhookUrl ? /* @__PURE__ */ jsx("span", { className: "text-muted text-sm", children: "Stored" }) : /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "danger", size: "sm", children: "Missing" });
        case "uniqueKey":
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "text-muted font-mono text-xs", children: `${String(row.uniqueKey).slice(0, 12)}…` }),
            /* @__PURE__ */ jsx(CopyToClipboard, { text: row.uniqueKey })
          ] });
        case "createdOnUtc":
          return row.createdOnUtc ? parseAndFormatDateTime(row.createdOnUtc) : "";
        case "actions":
          return /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-1", children: [
            canUpdate && // Wrapped in Tooltip.Trigger so the tooltip still explains a disabled button,
            // which takes no hover events of its own.
            /* @__PURE__ */ jsxs(Tooltip, { delay: 300, children: [
              /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx(
                Button,
                {
                  size: "sm",
                  isIconOnly: true,
                  variant: "tertiary",
                  "aria-label": testTooltip(row),
                  isPending: testingId === row.id,
                  isDisabled: !row.hasWebhookUrl || testingId != null,
                  onPress: () => onTest?.(row),
                  children: /* @__PURE__ */ jsx("i", { className: "ri-send-plane-line" })
                }
              ) }),
              /* @__PURE__ */ jsxs(Tooltip.Content, { showArrow: true, children: [
                /* @__PURE__ */ jsx(Tooltip.Arrow, {}),
                /* @__PURE__ */ jsx("p", { children: testTooltip(row) })
              ] })
            ] }),
            canUpdate && /* @__PURE__ */ jsx(
              Button,
              {
                size: "sm",
                isIconOnly: true,
                variant: "tertiary",
                "aria-label": "Edit",
                onPress: () => onEdit?.(row),
                children: /* @__PURE__ */ jsx("i", { className: "ri-pencil-line" })
              }
            ),
            canDelete && /* @__PURE__ */ jsx(
              DeleteButton,
              {
                size: "sm",
                isIconOnly: true,
                label: "Delete",
                confirmColor: "danger",
                onPress: () => onRemove?.(row)
              }
            )
          ] });
        default:
          return row[key] ?? "";
      }
    },
    [canUpdate, canDelete, testingId, onEdit, onRemove, onTest]
  );
  return /* @__PURE__ */ jsx(Table, { className: "h-full", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { className: "h-full", children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Teams destinations", children: [
    /* @__PURE__ */ jsx(Table.Header, { children: columns.map((column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: column.key === "name", id: column.key, children: column.label }, column.key)) }),
    /* @__PURE__ */ jsx(Table.Body, { items: destinations, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, children: columns.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.key) }, column.key)) }, row.id) })
  ] }) }) });
};
const INPUT_CLASS = "bg-surface-secondary text-foreground placeholder:text-muted w-full rounded-xl border border-transparent p-2.5 text-sm outline-none focus:border-accent";
const KIND_BUTTONS = [
  { key: String(TeamsDestinationKind.Channel), label: "Channel", color: "primary" },
  { key: String(TeamsDestinationKind.DirectMessage), label: "Direct Message", color: "primary" }
];
const KIND_HELP = {
  [TeamsDestinationKind.Channel]: "The flow posts every card to one fixed channel.",
  [TeamsDestinationKind.DirectMessage]: "The flow posts each card, as the Workflows bot, to the person the notification names. Reminders need a default Direct Message destination."
};
const formFrom = (destination) => ({
  kind: destination?.kind ?? TeamsDestinationKind.Channel,
  name: destination?.name ?? "",
  description: destination?.description ?? "",
  webhookUrl: "",
  isDefault: destination?.isDefault ?? false
});
const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};
const TeamsDestinationForm = reactive.component(
  ({ services, monitors, destination, onClose }) => {
    const [form, setForm] = useState(() => formFrom(destination));
    const saving = monitors.createDestination.executing || monitors.updateDestination.executing;
    const url = form.webhookUrl.trim();
    const urlValid = url ? isHttpsUrl(url) : Boolean(destination?.hasWebhookUrl);
    const valid = form.name.trim().length > 0 && urlValid;
    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    useSuccessReactor(services.teamsDestinations.createDestination, () => onClose?.());
    useSuccessReactor(services.teamsDestinations.updateDestination, () => onClose?.());
    const save = () => {
      if (!valid) return;
      const request = {
        kind: form.kind,
        name: form.name.trim(),
        description: form.description.trim() || void 0,
        webhookUrl: url || void 0,
        isDefault: form.isDefault
      };
      if (destination) {
        services.teamsDestinations.updateDestination(destination.id, request);
      } else {
        services.teamsDestinations.createDestination(request);
      }
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      destination && /* @__PURE__ */ jsx(TextFieldWithCopy, { label: "Unique Key", value: destination.uniqueKey, isReadOnly: true }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("label", { className: "text-foreground text-sm font-medium", children: "Kind" }),
        /* @__PURE__ */ jsx(
          ToggleButtons,
          {
            initialValue: String(form.kind),
            buttons: KIND_BUTTONS,
            onChange: (key) => key && update({ kind: Number(key) })
          }
        ),
        /* @__PURE__ */ jsx("p", { className: "text-muted text-xs", children: KIND_HELP[form.kind] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
          "Name ",
          /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsx(
          "input",
          {
            value: form.name,
            onChange: (event) => update({ name: event.target.value }),
            placeholder: form.kind === TeamsDestinationKind.Channel ? "e.g. Sales — Reminders" : "e.g. Direct messages",
            className: INPUT_CLASS
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("label", { className: "text-foreground text-sm font-medium", children: "Description" }),
        /* @__PURE__ */ jsx(
          "textarea",
          {
            value: form.description,
            onChange: (event) => update({ description: event.target.value }),
            placeholder: "Optional",
            rows: 3,
            className: `${INPUT_CLASS} resize-y`
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-foreground text-sm font-medium", children: [
          "Webhook URL ",
          !destination?.hasWebhookUrl && /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
        ] }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "password",
            autoComplete: "off",
            value: form.webhookUrl,
            onChange: (event) => update({ webhookUrl: event.target.value }),
            placeholder: destination?.hasWebhookUrl ? "Stored — leave empty to keep it" : "The flow's HTTP POST URL",
            className: INPUT_CLASS
          }
        ),
        /* @__PURE__ */ jsx("p", { className: `text-xs ${url && !urlValid ? "text-danger" : "text-muted"}`, children: url && !urlValid ? "Enter the full https:// URL from the Workflows trigger." : 'From the flow’s "When a Teams webhook request is received" trigger. It is stored encrypted and never shown again.' })
      ] }),
      /* @__PURE__ */ jsx(Checkbox, { isSelected: form.isDefault, onChange: (isDefault) => update({ isDefault }), children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
        /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
        /* @__PURE__ */ jsx(Label, { children: "Default for its kind" })
      ] }) }),
      /* @__PURE__ */ jsx("p", { className: "text-muted -mt-2 text-xs", children: "Used when a Teams notification names no destination. Making this the default clears the current default of the same kind." }),
      /* @__PURE__ */ jsxs(DrawerActions, { children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", isDisabled: saving, onPress: () => onClose?.(), children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            size: "sm",
            variant: "primary",
            isPending: saving,
            isDisabled: saving || !valid,
            onPress: save,
            children: destination ? "Save" : "Create"
          }
        )
      ] })
    ] });
  },
  {
    name: "TeamsDestinationForm",
    monitors: (services) => [
      services.teamsDestinations.createDestination,
      services.teamsDestinations.updateDestination
    ]
  }
);
TeamsDestinationForm.Drawer = withDrawerController(TeamsDestinationForm);
const TeamsDestinationsController = reactive.component(
  ({ services, monitors, data }) => {
    const { destinations = [] } = data;
    const runtime = useRuntimeProvider();
    const apiKey = runtime.config?.api?.apiKey ?? "";
    const can = (action) => runtime.user.hasPolicy(apiKey, "teamsdestinations", action);
    const [editing, setEditing] = useState(null);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const [testingId, setTestingId] = useState(null);
    const openForm = (destination) => {
      setEditing(destination);
      setFormKey((key) => key + 1);
      setDrawerOpen(true);
    };
    const remove = (destination) => services.teamsDestinations.deleteDestination(destination.id);
    useSuccessReactor(services.teamsDestinations.sendTest, () => setTestingId(null));
    useErrorReactor(services.teamsDestinations.sendTest, () => setTestingId(null));
    const test = (destination) => {
      setTestingId(destination.id);
      services.teamsDestinations.sendTest(destination.id);
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex min-h-0 flex-1 flex-col gap-3", children: [
      can("create") && /* @__PURE__ */ jsx("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", onPress: () => openForm(null), children: "Add destination" }) }),
      destinations.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: monitors.getDestinations.executing ? "Loading…" : "No Teams destinations yet." }) : /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1", children: /* @__PURE__ */ jsx(
        TeamsDestinationsTable,
        {
          destinations,
          canUpdate: can("update"),
          canDelete: can("delete"),
          testingId,
          onEdit: openForm,
          onRemove: remove,
          onTest: test
        }
      ) }),
      /* @__PURE__ */ jsx(
        TeamsDestinationForm.Drawer,
        {
          isOpen: isDrawerOpen,
          onClose: () => setDrawerOpen(false),
          size: "md",
          placement: "right",
          classNames: { body: "px-5 py-4" },
          destination: editing,
          headerContent: () => /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-semibold", children: editing ? "Edit Teams destination" : "New Teams destination" })
        },
        formKey
      )
    ] });
  },
  {
    name: "TeamsDestinationsController",
    monitors: (services) => [services.teamsDestinations.getDestinations],
    sync: (services) => [{ execute: () => services.teamsDestinations.getDestinations(), deps: [] }],
    queries: () => [
      {
        name: "destinations",
        defaultValue: [],
        execute: async (db2) => (await db2.teamsDestinations.toArray()).sort(
          (a, b) => a.kind - b.kind || a.name.localeCompare(b.name)
        ),
        deps: []
      }
    ]
  }
);
const SECTIONS = [
  {
    id: "campaigns",
    label: "Dialer Campaigns",
    resource: "dialers",
    action: "managecampaigns",
    description: "Manage each dialer's campaigns. The Unique Key is generated on save and used as the campaign key when sending Dialer notifications.",
    Content: CampaignsManager
  },
  {
    id: "teams",
    label: "Teams Channels",
    resource: "teamsdestinations",
    action: "view",
    description: "Manage the Power Automate Workflows webhooks that Teams notifications post through: one per channel, plus a default Direct Message flow for reminders. Send a test message to confirm a flow before relying on it.",
    Content: TeamsDestinationsController
  }
];
const SettingsView = () => {
  const runtime = useRuntimeProvider();
  const apiKey = runtime.config?.api?.apiKey ?? "";
  const sections = SECTIONS.filter(
    (section) => runtime.user.hasPolicy(apiKey, section.resource, section.action)
  );
  const [selected, setSelected] = useState(null);
  const active = sections.find((section) => section.id === selected) ?? sections[0];
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
    /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Messaging" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Settings" })
    ] }) }),
    /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "flex h-full flex-col gap-4 p-4", children: !active ? /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "You have no settings to manage." }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      sections.length > 1 && /* @__PURE__ */ jsx(
        Tabs,
        {
          className: "w-full overflow-x-auto",
          selectedKey: active.id,
          onSelectionChange: (key) => setSelected(String(key)),
          children: /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsx(Tabs.List, { "aria-label": "Settings sections", children: sections.map((section) => /* @__PURE__ */ jsxs(Tabs.Tab, { id: section.id, children: [
            section.label,
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] }, section.id)) }) })
        }
      ),
      /* @__PURE__ */ jsxs("section", { className: "flex min-h-0 flex-1 flex-col gap-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("h2", { className: "text-foreground text-lg font-semibold", children: active.label }),
          /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: active.description })
        ] }),
        /* @__PURE__ */ jsx(active.Content, {}, active.id)
      ] })
    ] }) }) })
  ] });
};
const SettingsRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(SettingsView, {}) }) });
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "notifications/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Notifications", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(NotificationsRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "opt-outs/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "OptOuts", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(OptOutsRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "batches/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "BulkNotifications", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(BatchesRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "settings/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Dialers", operation: "ManageCampaigns", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(SettingsRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Notifications", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(NotificationsRoutes, {}) })
    }
  )
] }) });
function getNotificationsNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "History",
        route: "/notifications",
        icon: "ri-notification-3-line",
        isVisible: user.hasPolicy(apiKey, "Notifications", "view")
      },
      {
        title: "Batches",
        route: "/batches",
        icon: "ri-stack-line",
        isVisible: user.hasPolicy(apiKey, "BulkNotifications", "view")
      },
      {
        title: "Opt-Outs",
        route: "/opt-outs",
        icon: "ri-user-unfollow-line",
        isVisible: user.hasPolicy(apiKey, "OptOuts", "view")
      },
      {
        title: "Settings",
        route: "/settings",
        icon: "ri-settings-3-line",
        isVisible: user.hasPolicy(apiKey, "Dialers", "managecampaigns") || user.hasPolicy(apiKey, "TeamsDestinations", "create")
      }
    ]
  };
}
function getNotificationsResources(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  const items = [];
  if (user.hasPolicy(apiKey, "notifications", "create")) {
    items.push({
      key: "notification",
      label: "New Notification",
      route: "/notifications",
      description: "Create new notification"
    });
  }
  return {
    items,
    handlers: ((navigate) => ({
      notification: () => navigate(RoutePaths().notifications.create())
    }))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const NOTIFICATIONS_LIST = "notifications.notifications-list";
const NotificationsList = ({
  customerId
}) => createElement(
  Embed,
  null,
  createElement(NotificationsListPanel, {
    accountId: customerId
  })
);
let appRuntime = null;
const initNotificationsRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("notifications", {
    description: {
      title: "Messaging",
      iconClass: "ri-notification-3-fill",
      defaultRoute: "/notifications",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getNotificationsNavigation,
    resources: getNotificationsResources,
    config: () => NotificationsConfig.current
  });
  hostRuntime.components.register(NOTIFICATIONS_LIST, NotificationsList);
};
export {
  initNotificationsRuntime
};
//# sourceMappingURL=index.js.map
